import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment-timezone'
import { Grid, CardContent, Typography, Box, Collapse, IconButton, TextField, MenuItem, Chip, Switch, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@material-ui/core'
import { components } from 'cng-web-lib'
import makeValidationSchema from './MakeValidationSchema'
import CngSection from '../../../components/cngcomponents/CngSection'
import { useFormContext } from 'react-hook-form'
import MpciStepper, { ProgressDots } from '../Common/components/MpciStepper'
import PartyMasterPickerDialog from '../Common/components/PartyMasterPickerDialog'
import VesselMasterPickerDialog from '../Common/components/VesselMasterPickerDialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  FILING_OPERATOR_MBL, DOC_TYPE_MBL, PROCESSING_INDICATOR, SHIPMENT_TYPE_MBL,
  EMIRATE
} from '../Common/constants/filingOptions'
import {
  VESSEL_OWNERSHIP, VESSEL_POWER_TYPE, PARTY_TYPE, PARTY_ID_TYPE,
  CONTAINER_SIZE, CONTAINER_LOAD_STATUS, VGM_UNIT,
  ESTABLISHMENT_OR_INDIVIDUAL, CONTACT_LEVEL,
  TEMPERATURE_TYPE, TEMPERATURE_UNIT, CONTAINER_TEMPERATURE_UNIT, SERVICE_REQUIREMENT,
  CARGO_TYPE, SEALING_PARTY, SEAL_TYPE
} from '../Common/constants/entityOptions'
import { COUNTRY_OPTIONS } from '../Common/constants/country'
import { CngTextFieldMandatory as CngTextField, CngDateFieldMandatory as CngDateField, CngDateTimeFieldMandatory as CngDateTimeField, CngSelectFieldMandatory as ClearableCngSelectField } from '../Common/components/MandatoryFieldWrappers'
import CodeMaintenanceSearchField from '../Common/components/CodeMaintenanceSearchField'
import SearchableCountryField from '../Common/components/SearchableCountryField'
import SearchableSelectField from '../Common/components/SearchableSelectField'
import OrgActingSelector from '../Common/components/OrgActingSelector'
import useOrgFilter from '../Common/hooks/useOrgFilter'
import {
  MONETARY_AMOUNT_TYPE, CURRENCY_CODE, CHARGE_CATEGORY,
  PAYMENT_ARRANGEMENT, WEIGHT_UNIT, PACKAGE_TYPE,
  NET_WEIGHT_UNIT, VOLUME_UNIT, PACKING_DESCRIPTION, HANDLING_INSTRUCTION_CODE,
  PACKING_INSTRUCTIONS_CODE, SPLIT_PACKAGE, DG_HAZARD_CLASS, DG_PACKAGING_DANGER_LEVEL
} from '../Common/constants/cargoOptions'

const {
  CngGridItem,
  button: { CngButton }
} = components

const MBL_STEPS = [
  { label: 'Bill Header' },
  { label: 'Vessel & Voyage' },
  { label: 'Consignment' },
  { label: 'Parties' },
  { label: 'Containers' },
  { label: 'Goods' },
  { label: 'Review & Submit' }
]

const DEFAULT_INITIAL_VALUES = Object.freeze({
  id: null,
  version: null,
  billType: 'M',
  onBehalfOfOrgId: null,
  filingOperator: '',
  documentType: '',
  processingIndicator: '',
  shipmentType: '',
  billNo: '',
  mpciDocId: '',
    shippingLineMpciPartyId: '',
  uaeFFSubColoaderMpciPartyId: '',
  placeOfBillIssueCountry: '',
  billIssueLocationName: '',
  bolIssueDate: '',
  freightPayableCountry: '',
  freightLocationName: '',
  sobDate: '',
  shipmentDescription: '',
  forwardingNotes: '',
  functionType: 'NEW',
  reasonForUpdatingFiling: '',
  // Step 2: Vessel Info (flat — re-nested into vesselInfo on submit)
  voyageNumber: '',
  carrierIdentifier: '',
  imoNumber: '',
  vesselName: '',
  vesselFlagCountry: '',
  vesselOwnershipIndicatorCode: '',
  vesselPowerTypeCode: '',
  vesselPowerTypeDescription: '',
  vesselLastForeignPortOfDeparture: '',
  vesselFirstUaePortOfArrival: '',
  etdAtTheLastForeignPort: '',
  atdAtTheLastForeignPort: '',
  etaAtTheFirstUaeSeaport: '',
            // Step 3: Consignment (flat — converted to consignmentDetails array on submit)
  portOfLoading: '',
  portOfDischarge: '',
  placeOfReceiptCountry: '',
  placeOfReceiptName: '',
  placeOfDeliveryCountry: '',
  placeOfDeliveryName: '',
  emirate: '',
  transshipmentPorts: [],
  monetaryDetails: [],
  // Step 4: Parties
  partiesInfo: [],
  // Step 5: Containers
  containerDetails: [],
  // Step 6: Goods
  goodsInfo: [],
  // Supporting documents (UPDATE mode)
  supportingDocuments: [],
  status: '',
  saveVesselMaster: false
})

const FORMIK_PROPS = {
  initialValues: { ...DEFAULT_INITIAL_VALUES },
  makeValidationSchema: makeValidationSchema
}

// All fields per step (for trigger)
const MBL_STEP_FIELDS = [
  ['filingOperator', 'documentType', 'processingIndicator', 'shipmentType', 'billNo',
   'shippingLineMpciPartyId', 'uaeFFSubColoaderMpciPartyId', 'placeOfBillIssueCountry',
   'billIssueLocationName', 'bolIssueDate', 'freightPayableCountry', 'freightLocationName',
   'sobDate', 'shipmentDescription', 'forwardingNotes'],
  ['voyageNumber', 'carrierIdentifier', 'imoNumber', 'vesselName', 'vesselFlagCountry',
   'vesselOwnershipIndicatorCode', 'vesselPowerTypeCode', 'vesselPowerTypeDescription',
   'vesselLastForeignPortOfDeparture', 'vesselFirstUaePortOfArrival',
   'etdAtTheLastForeignPort', 'atdAtTheLastForeignPort', 'etaAtTheFirstUaeSeaport'],
  ['portOfLoading', 'portOfDischarge', 'placeOfReceiptCountry', 'placeOfReceiptName',
   'placeOfDeliveryCountry', 'placeOfDeliveryName', 'emirate',
   'transshipmentPorts', 'monetaryDetails'],
  [],
  ['containerDetails'],
  ['goodsInfo'],
  []
]

// Required fields per step for hard gate check
const MBL_STEP_REQUIRED = [
  ['filingOperator', 'documentType', 'processingIndicator', 'shipmentType', 'billNo'],
  ['voyageNumber', 'carrierIdentifier', 'imoNumber', 'vesselName',
   'vesselLastForeignPortOfDeparture', 'vesselFirstUaePortOfArrival',
   'etaAtTheFirstUaeSeaport'],
  ['portOfLoading', 'portOfDischarge'],
  [],
  [],
  [],
  []
]

function validateForSubmit(values, { forDraft = false } = {}) {
  const errors = []
  const billHeaderLabels = { filingOperator: 'Filing Operator', documentType: 'Document Type', processingIndicator: 'Processing Indicator', shipmentType: 'Shipment Type', billNo: 'Master Bill No' }
  MBL_STEP_REQUIRED[0].forEach(f => { if (!values[f]) errors.push(`Bill Header: ${billHeaderLabels[f] || f} is required`) })
  if (!forDraft) {
    if (values.filingOperator === 'SA' && !values.shippingLineMpciPartyId) errors.push('Bill Header: Shipping Line is required for Shipping Agent (SA)')
    if (values.documentType === '704' && !values.uaeFFSubColoaderMpciPartyId) errors.push('Bill Header: UAE FF Sub/Coloader is required for FF Consolidation (704)')
  }
  const vesselLabels = { voyageNumber: 'Voyage Number', carrierIdentifier: 'Carrier Identifier', imoNumber: 'IMO Number', vesselName: 'Vessel Name', vesselLastForeignPortOfDeparture: 'Last Foreign Port of Departure', vesselFirstUaePortOfArrival: 'First UAE Port of Arrival', etaAtTheFirstUaeSeaport: 'ETA at First UAE Seaport' }
  MBL_STEP_REQUIRED[1].forEach(f => { if (!values[f]) errors.push(`Vessel & Voyage: ${vesselLabels[f] || f} is required`) })
  const consignmentLabels = { portOfLoading: 'Port of Loading', portOfDischarge: 'Port of Discharge', placeOfReceiptCountry: 'Place of Receipt', placeOfDeliveryCountry: 'Place of Delivery' }
  MBL_STEP_REQUIRED[2].forEach(f => { if (!values[f]) errors.push(`Consignment: ${consignmentLabels[f] || f} is required`) })
  if (!forDraft && values.shipmentType === '28' && (values.transshipmentPorts || []).length === 0) errors.push('Consignment: Port of Transshipment is required for Transshipment shipments')
  if (!forDraft) {
    const isNonNegotiable = values.processingIndicator === 'N'
    const _rawParties = values.partiesInfo || []
    const _mblFixed = ['CZ', 'CN']
    const _fixedPts = _mblFixed.map(t => _rawParties.find(p => p.partyType === t)).filter(Boolean)
    const _otherPts = _rawParties.filter(p => !_mblFixed.includes(p.partyType))
    ;[..._fixedPts, ..._otherPts].forEach((p, i) => {
      const lbl = MBL_PARTY_CONFIG[p.partyType]?.label ?? `Party #${i + 1}`
      if (!p.partyType) errors.push(`${lbl}: Party Type is required`)
      if (p.partyType && !['CZ', 'CN'].includes(p.partyType) && !p.partyIdentificationType) errors.push(`${lbl}: Party Identification Type is required`)
      if (p.partyType !== 'CN' && !p.partyName) errors.push(`${lbl}: Party Name is required`)
      if (p.partyType === 'CZ') {
        if (!p.address1) errors.push(`${lbl}: Address 1 is required`)
        if (!p.city)     errors.push(`${lbl}: City is required`)
        if (!p.country)  errors.push(`${lbl}: Country is required`)
      }
      if (p.partyType === 'CN' && isNonNegotiable) {
        if (!p.partyName)               errors.push(`${lbl}: Party Name is required`)
        if (!p.address1)                errors.push(`${lbl}: Address 1 is required`)
        if (!p.city)                    errors.push(`${lbl}: City is required`)
        if (!p.country)                 errors.push(`${lbl}: Country is required`)
        if (!p.partyIdentificationType) errors.push(`${lbl}: Party Identification Type is required`)
      }
      if (p.partyType === 'CN') {
        if (p.partyId && !p.establishmentOrIndividual) errors.push('Establishment or Individual is required when Consignee or Endorsee ID is provided.')
        if (p.establishmentOrIndividual && !p.partyId) errors.push('Consignee or Endorsee ID is required when Establishment or Individual is provided.')
      }
      ;(p.contactInfo || []).forEach((c, ci) => {
        if (!c.contactLevel) errors.push(`${lbl} Contact #${ci + 1}: Contact Level is required`)
        if (!c.contactName?.trim()) errors.push(`${lbl} Contact #${ci + 1}: Contact Name is required`)
      })
    })
  }
  const containers = values.containerDetails || []
  if (containers.length === 0) {
    errors.push('Containers: At least one container is required')
  } else {
    containers.forEach((c, i) => {
      const lbl = `Container #${i + 1}`
      if (!c.containerNumber) errors.push(`${lbl}: Container Number is required`)
      if (!c.containerSize)   errors.push(`${lbl}: Container Size is required`)
      if (!c.containerLoadStatus) errors.push(`${lbl}: Container Load Status is required`)
      if (!c.sealIdentifier)  errors.push(`${lbl}: Seal Identifier is required`)
      if (!forDraft) {
        if (c.containerVerifiedGrossMass && !/^[0-9]+$/.test(c.containerVerifiedGrossMass)) errors.push(`${lbl}: Verified Gross Mass must be a whole number`)
        if (c.containerVerifiedGrossMass && !c.containerVerifiedGrossMassUnit) errors.push(`${lbl}: Verified Gross Mass Unit is required when Gross Mass is filled`)
        if (c.containerTemperatureDegree && !/^-?[0-9]+$/.test(c.containerTemperatureDegree)) errors.push(`${lbl}: Container Temperature Degree must be a whole number`)
        if (c.containerTemperatureDegree && !c.temperatureMeasurementCode) errors.push(`${lbl}: Temperature Measurement Code is required when Temperature Degree is filled`)
      }
    })
  }
  const goods = values.goodsInfo || []
  const is705 = values.documentType === '705'
  if (goods.length === 0) {
    errors.push('Goods: At least one goods item is required')
  } else {
    goods.forEach((g, i) => {
      const lbl = `Goods #${i + 1}`
      if (!g.containerNumber)       errors.push(`${lbl}: Container Number is required`)
      if (!g.totalPackageQuantity)  errors.push(`${lbl}: Total Package Quantity is required`)
      if (!g.packageTypeDescription) errors.push(`${lbl}: Package Type is required`)
      if (!g.goodsItemGrossWeight)  errors.push(`${lbl}: Gross Weight is required`)
      if (!g.grossWeightUnitCode)   errors.push(`${lbl}: Gross Weight Unit is required`)
      if (!forDraft) {
        if (g.totalPackageQuantity && !/^[0-9]+$/.test(g.totalPackageQuantity)) errors.push(`${lbl}: Total Package Quantity must be a whole number`)
        if (g.goodsItemGrossWeight && !/^[0-9]+$/.test(g.goodsItemGrossWeight)) errors.push(`${lbl}: Gross Weight must be a whole number (no decimals)`)
        if (is705 && !g.hsCode)       errors.push(`${lbl}: HS Code is required`)
        if (is705 && !g.goodsItemDescription) errors.push(`${lbl}: Goods Description is required`)
        if (g.goodsItemNetWeight && !/^[0-9]+$/.test(g.goodsItemNetWeight)) errors.push(`${lbl}: Net Weight must be a whole number (no decimals)`)
        if (g.goodsItemNetWeight && !g.netWeightUnitCode) errors.push(`${lbl}: Net Weight Unit Code is required when Net Weight is filled`)
        if (g.volume && !/^[0-9]+$/.test(g.volume)) errors.push(`${lbl}: Volume must be a whole number (no decimals)`)
        if (g.volume && !g.volumeUnitCode) errors.push(`${lbl}: Volume Unit Code is required when Volume is filled`)
        ;(g.dangerousGoodsInfo || []).forEach((dg, di) => {
          const dgLbl = `${lbl} DG #${di + 1}`
          if (!dg.dangerousGoodsHazardClassCode) errors.push(`${dgLbl}: Hazard Class Code is required`)
          if (!dg.unitedNationsDangerousGoodsIdentifier) errors.push(`${dgLbl}: UN Dangerous Goods ID is required`)
          if (dg.shipmentFlashpointDegree && !dg.flashpointUnitCode) errors.push(`${dgLbl}: Flashpoint Unit Code is required when Flashpoint Degree is filled`)
        })
      }
    })
  }
  return errors
}

function Fields({ disabled, showNotification, shouldHideMap, isCreateMode, onDiscard, onSaveDraft, onSubmitToNaic, submitting, onActiveStepChange }) {
  const { setValue, getValues, watch, trigger, setError } = useFormContext()
  const [activeStep, setActiveStep] = useState(0)
  const [stepError, setStepError] = useState('')
  const [partyFieldErrors, setPartyFieldErrors] = useState({})
  const [goodsFieldErrors, setGoodsFieldErrors] = useState({})
  const [containerFieldErrors, setContainerFieldErrors] = useState({})
  const [monetaryFieldErrors, setMonetaryFieldErrors] = useState({})
  const [submitWarningOpen, setSubmitWarningOpen] = useState(false)
  const [submitWarnings, setSubmitWarnings] = useState([])
  const [draftWarningOpen, setDraftWarningOpen] = useState(false)
  const [draftWarnings, setDraftWarnings] = useState([])

  const partiesInfo = watch('partiesInfo') || []
  const containerDetails = watch('containerDetails') || []
  const goodsInfo = watch('goodsInfo') || []
  const monetaryDetails = watch('monetaryDetails') || []
  const transshipmentPorts = watch('transshipmentPorts') || []
  const processingIndicator = watch('processingIndicator')

  useEffect(() => {
    if (onActiveStepChange) onActiveStepChange(activeStep, activeStep === MBL_STEPS.length - 1)
  }, [activeStep]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleNext = async () => {
    if (disabled) {
      setActiveStep((prev) => Math.min(prev + 1, MBL_STEPS.length - 1))
      return
    }
    setStepError('')
    const values = getValues()
    let hasError = false

    // Trigger RHF Yup validation — returns false if any field fails
    const fieldsToValidate = MBL_STEP_FIELDS[activeStep]
    if (fieldsToValidate && fieldsToValidate.length > 0) {
      const triggerResult = await trigger(fieldsToValidate)
      if (!triggerResult) hasError = true
    }

    // Hard gate: manually check always-required flat fields
    const requiredFields = MBL_STEP_REQUIRED[activeStep] || []
    requiredFields.forEach((field) => {
      const val = values[field]
      if (val === null || val === undefined || val === '') {
        setError(field, { type: 'manual', message: 'Required' })
        hasError = true
      }
    })

    // Conditional hard gate for Step 1 MPCI Party ID fields
    if (activeStep === 0) {
      if (values.filingOperator === 'SA' && !values.shippingLineMpciPartyId) {
        setError('shippingLineMpciPartyId', { type: 'manual', message: 'Required for Shipping Agent (SA)' })
        hasError = true
      }
      if (values.documentType === '704' && !values.uaeFFSubColoaderMpciPartyId) {
        setError('uaeFFSubColoaderMpciPartyId', { type: 'manual', message: 'Required for FF Consolidation (704)' })
        hasError = true
      }
    }

    // Consignment step: transshipment required when shipmentType = '28'
    if (activeStep === 2) {
      const ports = values.transshipmentPorts || []
      if (values.shipmentType === '28' && ports.length === 0) {
        setStepError('Port of Transshipment is required when Shipment Type is Transshipment.')
        return
      }
      const monetary = values.monetaryDetails || []
      const monetaryErrs = {}
      monetary.forEach((m, i) => {
        const errs = {}
        if (m.monetaryAmount && !/^[0-9]{1,31}(\.[0-9]{1,2})?$/.test(m.monetaryAmount)) errs.monetaryAmount = 'Max 2 decimal places (e.g. 1250.50), max 33 digits'
        if (Object.keys(errs).length > 0) monetaryErrs[i] = errs
      })
      if (Object.keys(monetaryErrs).length > 0) {
        setMonetaryFieldErrors(monetaryErrs)
        hasError = true
      } else {
        setMonetaryFieldErrors({})
      }
    }

    // Parties step: validate required fields
    if (activeStep === 3) {
      const parties = values.partiesInfo || []
      const fieldErrors = {}
      const isNonNegotiable = values.processingIndicator === 'N'
      parties.forEach((p, i) => {
        if (p.partyType !== 'CN' && !p.partyName) {
          fieldErrors[i] = { ...fieldErrors[i], partyName: 'Required' }
        }
        if (p.partyType === 'CZ') {
          if (!p.address1) fieldErrors[i] = { ...fieldErrors[i], address1: 'Required' }
          if (!p.city)     fieldErrors[i] = { ...fieldErrors[i], city: 'Required' }
          if (!p.country)  fieldErrors[i] = { ...fieldErrors[i], country: 'Required' }
        }
        if (!['CZ', 'CN'].includes(p.partyType) || !p.partyType) {
          if (!p.partyType)                fieldErrors[i] = { ...fieldErrors[i], partyType: 'Required' }
          if (p.partyType && !p.partyIdentificationType) fieldErrors[i] = { ...fieldErrors[i], partyIdentificationType: 'Required' }
        }
        if (p.partyType === 'CN' && isNonNegotiable) {
          if (!p.partyName)               fieldErrors[i] = { ...fieldErrors[i], partyName: 'Required' }
          if (!p.address1)                fieldErrors[i] = { ...fieldErrors[i], address1: 'Required' }
          if (!p.city)                    fieldErrors[i] = { ...fieldErrors[i], city: 'Required' }
          if (!p.country)                 fieldErrors[i] = { ...fieldErrors[i], country: 'Required' }
          if (!p.partyIdentificationType) fieldErrors[i] = { ...fieldErrors[i], partyIdentificationType: 'Required' }
        }
        if (p.partyType === 'CN') {
          if (p.partyId && !p.establishmentOrIndividual) {
            fieldErrors[i] = { ...fieldErrors[i], establishmentOrIndividual: 'Establishment or Individual is required when Consignee or Endorsee ID is provided.' }
          }
          if (p.establishmentOrIndividual && !p.partyId) {
            fieldErrors[i] = { ...fieldErrors[i], partyId: 'Consignee or Endorsee ID is required when Establishment or Individual is provided.' }
          }
        }
        ;(p.contactInfo || []).forEach((c, ci) => {
          const cErrors = {}
          if (!c.contactLevel) cErrors.contactLevel = 'Required'
          if (!c.contactName?.trim()) cErrors.contactName = 'Required'
          if (Object.keys(cErrors).length > 0) {
            const contactInfoErrors = { ...(fieldErrors[i]?.contactInfo || {}), [ci]: { ...((fieldErrors[i]?.contactInfo || {})[ci] || {}), ...cErrors } }
            fieldErrors[i] = { ...fieldErrors[i], contactInfo: contactInfoErrors }
          }
        })
      })
      if (Object.keys(fieldErrors).length > 0) {
        setPartyFieldErrors(fieldErrors)
        return
      }
      setPartyFieldErrors({})
    }

    // Containers step: validate each item's required fields
    if (activeStep === 4) {
      const containers = values.containerDetails || []
      if (containers.length === 0) {
        setStepError('Please add at least one container before proceeding.')
        return
      }
      const fieldErrors = {}
      containers.forEach((c, i) => {
        const errs = {}
        if (!c.containerNumber) errs.containerNumber = 'Required'
        if (!c.containerSize) errs.containerSize = 'Required'
        if (!c.containerLoadStatus) errs.containerLoadStatus = 'Required'
        if (!c.sealIdentifier) errs.sealIdentifier = 'Required'
        if (c.containerVerifiedGrossMass && !/^[0-9]+$/.test(c.containerVerifiedGrossMass)) errs.containerVerifiedGrossMass = 'Must be a whole number'
        if (c.containerVerifiedGrossMass && !c.containerVerifiedGrossMassUnit) errs.containerVerifiedGrossMassUnit = 'Required when Gross Mass is filled'
        if (c.containerTemperatureDegree && !/^-?[0-9]+$/.test(c.containerTemperatureDegree)) errs.containerTemperatureDegree = 'Must be a whole number'
        if (c.containerTemperatureDegree && !c.temperatureMeasurementCode) errs.temperatureMeasurementCode = 'Required when Temperature Degree is filled'
        if (Object.keys(errs).length > 0) fieldErrors[i] = errs
      })
      if (Object.keys(fieldErrors).length > 0) {
        setContainerFieldErrors(fieldErrors)
        return
      }
      setContainerFieldErrors({})
    }

    // Goods step: validate each item's required fields
    if (activeStep === 5 && !validateGoodsStep(values)) return

    if (hasError) return
    setActiveStep((prev) => Math.min(prev + 1, MBL_STEPS.length - 1))
  }
  function validateGoodsStep(values) {
    const goods = values.goodsInfo || []
    if (goods.length === 0) {
      setStepError('Please add at least one goods item before proceeding.')
      return false
    }
    const is705 = values.documentType === '705'
    const fieldErrors = {}
    goods.forEach((g, i) => {
      const errs = {}
      if (!g.containerNumber) errs.containerNumber = 'Required'
      if (!g.totalPackageQuantity) errs.totalPackageQuantity = 'Required'
      if (!g.packageTypeDescription) errs.packageTypeDescription = 'Required'
      if (!g.goodsItemGrossWeight) errs.goodsItemGrossWeight = 'Required'
      else if (!/^[0-9]+$/.test(g.goodsItemGrossWeight)) errs.goodsItemGrossWeight = 'Must be a whole number'
      if (!g.grossWeightUnitCode) errs.grossWeightUnitCode = 'Required'
      if (is705 && !g.hsCode) errs.hsCode = 'Required'
      if (is705 && !g.goodsItemDescription) errs.goodsItemDescription = 'Required'
      if (g.goodsItemNetWeight && !/^[0-9]+$/.test(g.goodsItemNetWeight)) errs.goodsItemNetWeight = 'Must be a whole number'
      if (g.goodsItemNetWeight && !g.netWeightUnitCode) errs.netWeightUnitCode = 'Required when Net Weight is filled'
      if (g.volume && !/^[0-9]+$/.test(g.volume)) errs.volume = 'Must be a whole number'
      if (g.volume && !g.volumeUnitCode) errs.volumeUnitCode = 'Required when Volume is filled'
      ;(g.dangerousGoodsInfo || []).forEach((dg, di) => {
        const dgErrs = {}
        if (!dg.dangerousGoodsHazardClassCode) dgErrs.dangerousGoodsHazardClassCode = 'Required'
        if (!dg.unitedNationsDangerousGoodsIdentifier) dgErrs.unitedNationsDangerousGoodsIdentifier = 'Required'
        if (dg.shipmentFlashpointDegree && !dg.flashpointUnitCode) dgErrs.flashpointUnitCode = 'Required when Flashpoint Degree is filled'
        if (Object.keys(dgErrs).length > 0) {
          if (!errs.dangerousGoodsInfo) errs.dangerousGoodsInfo = {}
          errs.dangerousGoodsInfo[di] = dgErrs
        }
      })
      if (Object.keys(errs).length > 0) fieldErrors[i] = errs
    })
    if (Object.keys(fieldErrors).length > 0) {
      setGoodsFieldErrors(fieldErrors)
      return false
    }
    setGoodsFieldErrors({})
    return true
  }
  function handleSaveDraft() {
    const warnings = validateForSubmit(getValues(), { forDraft: true })
    if (warnings.length > 0) {
      setDraftWarnings(warnings)
      setDraftWarningOpen(true)
    } else {
      onSaveDraft()
    }
  }
  function handleSubmitToNaic() {
    const errors = validateForSubmit(getValues())
    if (errors.length > 0) {
      setSubmitWarnings(errors)
      setSubmitWarningOpen(true)
      return
    }
    onSubmitToNaic()
  }
  const handleBack = () => {
    setStepError('')
    setActiveStep((prev) => Math.max(prev - 1, 0))
  }

    // --- List helpers for repeatable inline entries ---
  function addListItem(fieldName, newItem) {
    const list = [...(getValues(fieldName) || []), newItem].map((item, i) => ({ ...item, _id: i + 1 }))
    setValue(fieldName, list, { shouldValidate: true })
  }
  function removeListItem(fieldName, index) {
    const list = [...(getValues(fieldName) || [])]
    list.splice(index, 1)
    setValue(fieldName, list.map((item, i) => ({ ...item, _id: i + 1 })), { shouldValidate: true })
  }
  function updateListItem(fieldName, index, updatedItem) {
    const list = [...(getValues(fieldName) || [])]
    list[index] = { ...list[index], ...updatedItem }
    setValue(fieldName, list, { shouldValidate: true })
  }
  function clearPartyFieldError(idx, fieldName) {
    setPartyFieldErrors(prev => {
      const next = { ...prev }
      if (next[idx]) {
        delete next[idx][fieldName]
        if (Object.keys(next[idx]).length === 0) delete next[idx]
      }
      return next
    })
  }
  function clearGoodsFieldError(idx, fieldName) {
    setGoodsFieldErrors(prev => {
      const next = { ...prev }
      if (next[idx]) {
        delete next[idx][fieldName]
        if (Object.keys(next[idx]).length === 0) delete next[idx]
      }
      return next
    })
  }
  function clearContainerFieldError(idx, fieldName) {
    setContainerFieldErrors(prev => {
      const next = { ...prev }
      if (next[idx]) {
        delete next[idx][fieldName]
        if (Object.keys(next[idx]).length === 0) delete next[idx]
      }
      return next
    })
  }
  function clearMonetaryFieldError(idx, fieldName) {
    setMonetaryFieldErrors(prev => {
      const next = { ...prev }
      if (next[idx]) {
        delete next[idx][fieldName]
        if (Object.keys(next[idx]).length === 0) delete next[idx]
      }
      return next
    })
  }
  function clearDgFieldError(goodsIdx, dgIdx, fieldName) {
    setGoodsFieldErrors(prev => {
      const next = { ...prev }
      const dgInfo = next[goodsIdx]?.dangerousGoodsInfo
      if (dgInfo?.[dgIdx]) {
        const dgErrs = { ...dgInfo[dgIdx] }
        delete dgErrs[fieldName]
        const updatedDgInfo = { ...dgInfo }
        if (Object.keys(dgErrs).length === 0) delete updatedDgInfo[dgIdx]
        else updatedDgInfo[dgIdx] = dgErrs
        next[goodsIdx] = { ...next[goodsIdx], dangerousGoodsInfo: Object.keys(updatedDgInfo).length === 0 ? undefined : updatedDgInfo }
      }
      return next
    })
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <Step1BillHeader disabled={disabled} setValue={setValue} isCreateMode={isCreateMode} />
      case 1:
        return <Step2VesselVoyage disabled={disabled} setValue={setValue} />
            case 2:
        return <Step3Consignment disabled={disabled} setValue={setValue} getValues={getValues} monetaryDetails={monetaryDetails} transshipmentPorts={transshipmentPorts} addListItem={addListItem} removeListItem={removeListItem} monetaryFieldErrors={monetaryFieldErrors} clearMonetaryFieldError={clearMonetaryFieldError} />
      case 3:
        return <Step4Parties disabled={disabled} setValue={setValue} getValues={getValues} partiesInfo={partiesInfo} addListItem={addListItem} removeListItem={removeListItem} partyFieldErrors={partyFieldErrors} clearPartyFieldError={clearPartyFieldError} processingIndicator={processingIndicator} />
      case 4:
        return <Step5Containers disabled={disabled} setValue={setValue} getValues={getValues} containerDetails={containerDetails} addListItem={addListItem} removeListItem={removeListItem} containerFieldErrors={containerFieldErrors} clearContainerFieldError={clearContainerFieldError} />
            case 5:
        return <Step6Goods disabled={disabled} setValue={setValue} getValues={getValues} goodsInfo={goodsInfo} containerDetails={containerDetails} addListItem={addListItem} removeListItem={removeListItem} goodsFieldErrors={goodsFieldErrors} clearGoodsFieldError={clearGoodsFieldError} clearDgFieldError={clearDgFieldError} />
      case 6:
        return <Step7ReviewSubmit getValues={getValues} />
      default:
        return null
    }
  }

        const isLastStep = activeStep === MBL_STEPS.length - 1

  const footerContent = (
    <Box display='flex' alignItems='center' style={{ width: '100%', gap: 8 }}>
      {/* Left — Back + Discard (create only) */}
      <Box display='flex' alignItems='center' style={{ gap: 8 }}>
        {activeStep > 0 && (
          <CngButton
            color='secondary'
            onClick={handleBack}
            size='medium'
            startIcon={<FontAwesomeIcon icon={['fal', 'arrow-left']} />}
          >
            Back
          </CngButton>
        )}
        {isCreateMode && !disabled && onDiscard && (
          <CngButton
            color='secondary'
            size='medium'
            onClick={onDiscard}
            startIcon={<FontAwesomeIcon icon={['fal', 'xmark']} />}
          >
            Discard
          </CngButton>
        )}
      </Box>

      {/* Center — Progress Dots */}
      <Box flex={1} display='flex' justifyContent='center'>
        <ProgressDots total={MBL_STEPS.length} activeStep={activeStep} />
      </Box>

      {/* Right — Save as Draft (create only) + Next / Submit */}
      <Box display='flex' alignItems='center' style={{ gap: 8 }}>
        {isCreateMode && !disabled && (
          <CngButton
            color='secondary'
            size='medium'
            disabled={!!submitting}
            shouldShowProgress={!!submitting}
            onClick={handleSaveDraft}
            startIcon={<FontAwesomeIcon icon={['fal', 'floppy-disk']} />}
          >
            Save as Draft
          </CngButton>
        )}
                {isLastStep && isCreateMode ? (
          <CngButton
            color='primary'
            size='medium'
            disabled={!!submitting}
            shouldShowProgress={!!submitting}
            onClick={handleSubmitToNaic}
            startIcon={<FontAwesomeIcon icon={['fal', 'paper-plane']} />}
          >
            Validate & Submit
          </CngButton>
        ) : (
          <CngButton
            color='primary'
            onClick={handleNext}
            size='medium'
            disabled={isLastStep}
            endIcon={<FontAwesomeIcon icon={['fal', 'arrow-right']} />}
          >
            Next
          </CngButton>
        )}
      </Box>
    </Box>
  )

        return (
    <>
      <MpciStepper
        steps={MBL_STEPS}
        activeStep={activeStep}
        onStepChange={setActiveStep}
        footer={footerContent}
      >
        {stepError && (
          <Box mb={2} display='flex' alignItems='flex-start' style={{ gap: 10, padding: '10px 14px', background: '#fdecea', border: '1px solid #f44336', borderRadius: 8 }}>
            <FontAwesomeIcon icon={['fal', 'circle-exclamation']} style={{ fontSize: 14, marginTop: 2, flexShrink: 0, color: '#d32f2f' }} />
            <Typography variant='caption' style={{ color: '#d32f2f' }}>{stepError}</Typography>
          </Box>
        )}
        {renderStepContent()}
      </MpciStepper>
      <Dialog open={draftWarningOpen} onClose={() => setDraftWarningOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Save as Draft</DialogTitle>
        <DialogContent>
          <Typography variant='body2' style={{ marginBottom: 8 }}>The following required fields are not yet filled. You can still save as draft and complete them later.</Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {draftWarnings.map((w, i) => <li key={i}><Typography variant='body2'>{w}</Typography></li>)}
          </ul>
        </DialogContent>
        <DialogActions>
          <CngButton color='secondary' onClick={() => setDraftWarningOpen(false)}>Cancel</CngButton>
          <CngButton onClick={() => { setDraftWarningOpen(false); onSaveDraft() }}>OK, Save Draft</CngButton>
        </DialogActions>
      </Dialog>
      <Dialog open={submitWarningOpen} onClose={() => setSubmitWarningOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Validate & Submit</DialogTitle>
        <DialogContent>
          <Typography variant='body2' style={{ marginBottom: 8 }}>Please fix the following issues before submitting:</Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {submitWarnings.map((w, i) => <li key={i}><Typography variant='body2'>{w}</Typography></li>)}
          </ul>
        </DialogContent>
        <DialogActions>
          <CngButton color='secondary' onClick={() => setSubmitWarningOpen(false)}>Cancel</CngButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

// Resolves the display label from an items array for a given value.
// Used to show the correct label in view (disabled) mode for CngSelectField.
function resolveLabel(items, value) {
  if (!value) return value
  const match = items.find((i) => i.value === value)
  return match ? match.text : value
}

function Step1BillHeader({ disabled, setValue, isCreateMode }) {
  const { watch, getValues } = useFormContext()
  const { canActAsOtherOrg } = useOrgFilter()
  const [additionalOpen, setAdditionalOpen] = useState(() => {
    const v = getValues()
    return !!(v.placeOfBillIssueCountry || v.billIssueLocationName || v.freightPayableCountry || v.freightLocationName || v.bolIssueDate || v.sobDate || v.shipmentDescription || v.forwardingNotes)
  })
  const [billIssuanceMode, setBillIssuanceMode] = useState(() => {
    const v = getValues('placeOfBillIssueCountry')
    return v && v.length <= 2 ? 'country' : 'unlocode'
  })
  const [freightPaymentMode, setFreightPaymentMode] = useState(() => {
    const v = getValues('freightPayableCountry')
    return v && v.length <= 2 ? 'country' : 'unlocode'
  })
  const filingOperator = watch('filingOperator')
  const documentType = watch('documentType')
  const processingIndicator = watch('processingIndicator')
  const shipmentType = watch('shipmentType')
  const isSA = filingOperator === 'SA'
  const is704FF = documentType === '704'

    // Auto-clear MPCI Party ID fields when the controlling condition is no longer met
  // useRef guards prevent clearing on initial mount (would wipe pre-filled/cloned values)
  const isMountedSA = useRef(false)
  useEffect(() => {
    if (!isMountedSA.current) { isMountedSA.current = true; return }
    if (!isSA) setValue('shippingLineMpciPartyId', '', { shouldValidate: false })
  }, [isSA])

  const isMounted704 = useRef(false)
  useEffect(() => {
    if (!isMounted704.current) { isMounted704.current = true; return }
    if (!is704FF) setValue('uaeFFSubColoaderMpciPartyId', '', { shouldValidate: false })
  }, [is704FF])

  function handleBillIssuanceModeChange(mode) {
    setBillIssuanceMode(mode)
    setValue('placeOfBillIssueCountry', '', { shouldValidate: false })
    setValue('billIssueLocationName', '', { shouldValidate: false })
  }

  function handleFreightPaymentModeChange(mode) {
    setFreightPaymentMode(mode)
    setValue('freightPayableCountry', '', { shouldValidate: false })
    setValue('freightLocationName', '', { shouldValidate: false })
  }

  return (
    <>
    <CngSection title='Bill Header'>
      <CardContent>
        <Grid container spacing={1}>
          {canActAsOtherOrg && (
            <CngGridItem xs={12} sm={6} lg={4}>
              <OrgActingSelector name='onBehalfOfOrgId' disabled={disabled || !isCreateMode} />
            </CngGridItem>
          )}
                                        <CngGridItem xs={12} sm={6} lg={4}>
            <ClearableCngSelectField
              required
              name='filingOperator'
              label='Filing Role'
              disabled={disabled}
              size='small'
              items={FILING_OPERATOR_MBL}
            />
          </CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}>
            <ClearableCngSelectField
              required
              name='documentType'
              label={<>Freight Forwarder Consolidation{' '}<Tooltip title='Indicates whether the shipment is consolidated under one or more freight forwarder Bills of Lading (FF BLs).'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>}
              disabled={disabled}
              size='small'
              items={DOC_TYPE_MBL}
            />
          </CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}>
            <ClearableCngSelectField
              required
              name='processingIndicator'
              label='Processing Indicator'
              disabled={disabled}
              size='small'
              items={PROCESSING_INDICATOR}
            />
          </CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}>
            <ClearableCngSelectField
              required
              name='shipmentType'
              label='Shipment Type'
              disabled={disabled}
              size='small'
              items={SHIPMENT_TYPE_MBL}
            />
          </CngGridItem>
                                                  <CngGridItem xs={12} sm={6} lg={4}>
            <CngTextField
              required
              name='billNo'
              label='Master Bill of Lading Number'
              disabled={disabled}
              size='small'
              inputProps={{ maxLength: 70 }}
              onChange={(e) => setValue('billNo', e.target.value.toUpperCase(), { shouldValidate: true })}
                        />
          </CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}>
            <CngTextField
              name='mpciDocId'
              label={<>MPCI Document ID / Transaction ID{' '}<Tooltip title="System-generated 16-digit transaction ID with 'NAIC' prefix, uniquely identifying each submission."><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>}
              disabled={true}
              size='small'
            />
          </CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}>
            <CngTextField
              required={isSA}
              name='shippingLineMpciPartyId'
              label='Shipping Line MPCI Party Id'
              disabled={disabled || !isSA}
              size='small'
              inputProps={{ maxLength: 7 }}
              onChange={(e) => setValue('shippingLineMpciPartyId', e.target.value.toUpperCase(), { shouldValidate: true })}
            />
          </CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}>
            <CngTextField
              required={is704FF}
              name='uaeFFSubColoaderMpciPartyId'
              label={<>UAE FF/Sub Co-loader MPCI Party Id{' '}<Tooltip title='Enter the MPCI Party ID of the UAE-based Freight Forwarder or Sub Co-loader responsible for filing the lower-level Bill of Lading.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>}
              disabled={disabled || !is704FF}
              size='small'
              inputProps={{ maxLength: 7 }}
              onChange={(e) => setValue('uaeFFSubColoaderMpciPartyId', e.target.value.toUpperCase(), { shouldValidate: true })}
            />
          </CngGridItem>
                    {/* optional fields moved to Additional Header Info collapsible below */}

                              {/* Function Type + Reason for Updating Filing are rendered in the UpdateFilingBanner (EditPage) for update mode */}
        </Grid>
      </CardContent>
    </CngSection>

    {/* Additional Header Info collapsible */}
    <Box mt={2}>
      <Box onClick={() => setAdditionalOpen(!additionalOpen)} display='flex' alignItems='center'
        style={{ padding: '10px 14px', background: '#f5f4f0', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.09)', gap: 8 }}>
        <FontAwesomeIcon icon={['fal', 'sliders']} style={{ fontSize: 14, flexShrink: 0 }} />
        <Typography variant='subtitle2' style={{ flex: 1 }}>Additional Header Info</Typography>
        <Chip label='Optional' size='small' style={{ fontSize: 11, height: 22 }} />
        <FontAwesomeIcon icon={['fal', additionalOpen ? 'chevron-up' : 'chevron-down']} style={{ fontSize: 13 }} />
      </Box>
      <Collapse in={additionalOpen}>
        <Box mt={1} p={2} border={1} borderColor='divider' borderRadius={8}>
          <Grid container spacing={1} alignItems='flex-start'>
            <CngGridItem xs={12} sm={6} lg={6}>
              <Box>
                <Box display='flex' alignItems='center' style={{ gap: 4, marginBottom: 4 }}>
                  <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 11 }}>Place of bill issuance</Typography>
                </Box>
                <Box display='flex' style={{ gap: 4, marginBottom: 6 }}>
                  {['unlocode', 'country'].map((mode) => (
                    <Box
                      key={mode}
                      component='button'
                      type='button'
                      onClick={() => !disabled && handleBillIssuanceModeChange(mode)}
                      style={{
                        padding: '2px 10px', fontSize: 12,
                        fontWeight: billIssuanceMode === mode ? 600 : 400,
                        background: billIssuanceMode === mode ? '#1a2744' : '#fff',
                        color: billIssuanceMode === mode ? '#fff' : 'rgba(0,0,0,0.7)',
                        border: '1px solid rgba(0,0,0,0.23)', borderRadius: 4,
                        cursor: disabled ? 'default' : 'pointer', lineHeight: '20px',
                        whiteSpace: 'nowrap', outline: 'none'
                      }}
                    >
                      {mode === 'unlocode' ? 'UN/LOCODE' : 'Country + location'}
                    </Box>
                  ))}
                </Box>
                {billIssuanceMode === 'unlocode' ? (
                  <CodeMaintenanceSearchField codeType='UNLOC' name='placeOfBillIssueCountry' label='Search UN/LOCODE...' disabled={disabled} size='small' />
                ) : (
                  <Box display='flex' style={{ gap: 4 }}>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <SearchableCountryField name='placeOfBillIssueCountry' label='Country' disabled={disabled} />
                    </Box>
                    <Box style={{ flex: 2, minWidth: 0 }}>
                      <CngTextField name='billIssueLocationName' label='Location name' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} />
                    </Box>
                  </Box>
                )}
                <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 11 }}>
                  Select a UN/LOCODE, or switch to enter country code + location name
                </Typography>
              </Box>
            </CngGridItem>
            <CngGridItem xs={12} sm={6} lg={6}>
              <Box>
                <Box display='flex' alignItems='center' style={{ gap: 4, marginBottom: 4 }}>
                  <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 11 }}>Place of freight payment</Typography>
                </Box>
                <Box display='flex' style={{ gap: 4, marginBottom: 6 }}>
                  {['unlocode', 'country'].map((mode) => (
                    <Box
                      key={mode}
                      component='button'
                      type='button'
                      onClick={() => !disabled && handleFreightPaymentModeChange(mode)}
                      style={{
                        padding: '2px 10px', fontSize: 12,
                        fontWeight: freightPaymentMode === mode ? 600 : 400,
                        background: freightPaymentMode === mode ? '#1a2744' : '#fff',
                        color: freightPaymentMode === mode ? '#fff' : 'rgba(0,0,0,0.7)',
                        border: '1px solid rgba(0,0,0,0.23)', borderRadius: 4,
                        cursor: disabled ? 'default' : 'pointer', lineHeight: '20px',
                        whiteSpace: 'nowrap', outline: 'none'
                      }}
                    >
                      {mode === 'unlocode' ? 'UN/LOCODE' : 'Country + location'}
                    </Box>
                  ))}
                </Box>
                {freightPaymentMode === 'unlocode' ? (
                  <CodeMaintenanceSearchField codeType='UNLOC' name='freightPayableCountry' label='Search UN/LOCODE...' disabled={disabled} size='small' />
                ) : (
                  <Box display='flex' style={{ gap: 4 }}>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <SearchableCountryField name='freightPayableCountry' label='Country' disabled={disabled} />
                    </Box>
                    <Box style={{ flex: 2, minWidth: 0 }}>
                      <CngTextField name='freightLocationName' label='Location name' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} />
                    </Box>
                  </Box>
                )}
                <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 11 }}>
                  Select a UN/LOCODE, or switch to enter country code + location name
                </Typography>
              </Box>
            </CngGridItem>
            <CngGridItem xs={12} sm={4} lg={4}><CngDateTimeField name='bolIssueDate' label='Bill issue date' disabled={disabled} size='small' format='YYYY-MM-DD HH:mm' /></CngGridItem>
            <CngGridItem xs={12} sm={4} lg={4}><CngDateTimeField name='sobDate' label='Shipped-on-board date' disabled={disabled} size='small' format='YYYY-MM-DD HH:mm' /></CngGridItem>
            <CngGridItem xs={12} sm={4} lg={4}><CngTextField name='shipmentDescription' label='Shipment description' disabled={disabled} size='small' inputProps={{ maxLength: 35 }} /></CngGridItem>
            <CngGridItem xs={12} lg={12}><CngTextField name='forwardingNotes' label={<>Forwarding Notes{' '}<Tooltip title='Provide any forwarding or export-related references, instructions, or additional notes relevant to this shipment.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' inputProps={{ maxLength: 512 }} multiline rows={2} /></CngGridItem>
          </Grid>
        </Box>
      </Collapse>
    </Box>
    </>
  )
}

function Step2VesselVoyage({ disabled, setValue }) {
  const [vesselPickerOpen, setVesselPickerOpen] = useState(false)
  const { watch } = useFormContext()
  const saveVesselMaster = watch('saveVesselMaster')

  function handleVesselSelect(vessel) {
    // Auto-fill vessel fields from selected vessel master record
    // Voyage number is filing-specific — NOT auto-filled
    if (vessel.carrierIdentifier) setValue('carrierIdentifier', vessel.carrierIdentifier, { shouldValidate: true })
    if (vessel.imoNumber) setValue('imoNumber', vessel.imoNumber, { shouldValidate: true })
    if (vessel.vesselName) setValue('vesselName', vessel.vesselName, { shouldValidate: true })
    if (vessel.vesselFlagCountry) setValue('vesselFlagCountry', vessel.vesselFlagCountry, { shouldValidate: true })
    if (vessel.vesselOwnershipIndicatorCode) setValue('vesselOwnershipIndicatorCode', vessel.vesselOwnershipIndicatorCode, { shouldValidate: true })
    if (vessel.vesselPowerTypeCode) setValue('vesselPowerTypeCode', vessel.vesselPowerTypeCode, { shouldValidate: true })
    if (vessel.vesselPowerTypeDescription) setValue('vesselPowerTypeDescription', vessel.vesselPowerTypeDescription, { shouldValidate: true })
    if (vessel.vesselLastForeignPortOfDeparture) setValue('vesselLastForeignPortOfDeparture', vessel.vesselLastForeignPortOfDeparture, { shouldValidate: true })
    if (vessel.vesselFirstPortOfArrival) setValue('vesselFirstUaePortOfArrival', vessel.vesselFirstPortOfArrival, { shouldValidate: true })
  }

  return (
    <>
    <CngSection title='Vessel & Voyage'>
      <CardContent>
        <Grid container spacing={1}>
          <CngGridItem xs={12} sm={6} lg={4}><CngTextField required name='voyageNumber' label='Voyage Number' disabled={disabled} size='small' inputProps={{ maxLength: 17 }} /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CodeMaintenanceSearchField required codeType='SMDG' name='carrierIdentifier' label={<>Carrier Identifier{' '}<Tooltip title='Enter the 3-character carrier code (SMDG).'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' separator=': ' /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CngTextField required name='imoNumber' label={<>IMO Number{' '}<Tooltip title='Provide the vessel&#39;s 7-digit IMO number (exclude the &#39;IMO&#39; prefix).'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' inputProps={{ maxLength: 7 }} onChange={(e) => setValue('imoNumber', e.target.value.replace(/[^0-9]/g, ''), { shouldValidate: true })} /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CngTextField required name='vesselName' label='Vessel Name' disabled={disabled} size='small' inputProps={{ maxLength: 70 }} /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><SearchableCountryField name='vesselFlagCountry' label='Vessel Flag Country' disabled={disabled} /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><ClearableCngSelectField name='vesselOwnershipIndicatorCode' label='Vessel Ownership Indicator Code' disabled={disabled} size='small' items={VESSEL_OWNERSHIP} /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><ClearableCngSelectField name='vesselPowerTypeCode' label='Vessel Power Type Code' disabled={disabled} size='small' items={VESSEL_POWER_TYPE} /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CngTextField name='vesselPowerTypeDescription' label='Vessel Power Type Description' disabled={disabled} size='small' inputProps={{ maxLength: 17 }} /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CodeMaintenanceSearchField required codeType='UNLOC' name='vesselLastForeignPortOfDeparture' label='Vessel Last Foreign Port of Departure' disabled={disabled} size='small' /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CodeMaintenanceSearchField required codeType='UNLOC' name='vesselFirstUaePortOfArrival' label='Vessel First UAE Port of Arrival' disabled={disabled} size='small' /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CngDateTimeField name='etdAtTheLastForeignPort' label={<>ETD at the Last Foreign Port{' '}<Tooltip title='Enter the estimated date and time of departure from the last foreign port prior to arrival in the UAE.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' format='YYYY-MM-DD HH:mm' /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CngDateTimeField name='atdAtTheLastForeignPort' label={<>ATD at the Last Foreign Port{' '}<Tooltip title='Enter the actual date and time of departure from the last foreign port prior to arrival in the UAE.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' format='YYYY-MM-DD HH:mm' /></CngGridItem>
          <CngGridItem xs={12} sm={6} lg={4}><CngDateTimeField required name='etaAtTheFirstUaeSeaport' label={<>ETA at the First UAE Seaport{' '}<Tooltip title='Enter the estimated date and time of arrival at the first port of entry in the UAE.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' format='YYYY-MM-DD HH:mm' /></CngGridItem>
        </Grid>
                        {!disabled && (
          <Box mt={2} display='flex' alignItems='center' style={{ gap: 8 }}>
            <CngButton color='secondary' size='small' variant='outlined' onClick={() => setVesselPickerOpen(true)} startIcon={<FontAwesomeIcon icon={['fal', 'ship']} />}>
              Pick from Vessel Master
            </CngButton>
          </Box>
        )}
        {!disabled && (
          <Box mt={2} display='flex' alignItems='center'
            style={{ padding: '8px 12px', background: '#fafafa', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 8, gap: 8 }}>
            <FontAwesomeIcon icon={['fal', 'bookmark']} style={{ fontSize: 13, opacity: 0.5 }} />
            <Typography variant='caption' style={{ flex: 1 }}>Save vessel to Vessel Master</Typography>
            <Switch size='small' checked={!!saveVesselMaster} onChange={(e) => setValue('saveVesselMaster', e.target.checked)} />
          </Box>
        )}
      </CardContent>
    </CngSection>

    <VesselMasterPickerDialog
      open={vesselPickerOpen}
      onClose={() => setVesselPickerOpen(false)}
      onSelect={handleVesselSelect}
    />

</>
  )
}

function getCodeValue(val) {
  if (val === null || val === undefined) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'object' && val.code !== undefined) return val.code
  return ''
}

function Step3Consignment({ disabled, setValue, getValues, monetaryDetails, transshipmentPorts, addListItem, removeListItem, monetaryFieldErrors = {}, clearMonetaryFieldError }) {
  const { watch } = useFormContext()
  const shipmentType = watch('shipmentType')
  const isTransshipRequired = shipmentType === '28'
  const [transshipOpen, setTransshipOpen] = useState(() => (transshipmentPorts || []).some(p => p.unlocCode))
  useEffect(() => { if (isTransshipRequired) setTransshipOpen(true) }, [isTransshipRequired])
  const [monetaryOpen, setMonetaryOpen] = useState(() => (monetaryDetails || []).some(m => m.monetaryAmountType || m.monetaryAmount != null || m.currencyCode || m.chargeCategoryCode || m.paymentArrangementCode))
  const [placeOfReceiptMode, setPlaceOfReceiptMode] = useState(() => {
    const v = getValues('placeOfReceiptCountry')
    return v && v.length <= 2 ? 'country' : 'unlocode'
  })
  const [placeOfDeliveryMode, setPlaceOfDeliveryMode] = useState(() => {
    const v = getValues('placeOfDeliveryCountry')
    return v && v.length <= 2 ? 'country' : 'unlocode'
  })

  function updateField(fieldName, index, field, value) {
    const list = [...(getValues(fieldName) || [])]
    list[index] = { ...list[index], [field]: value }
    setValue(fieldName, list, { shouldValidate: false })
  }
  function handlePlaceOfReceiptModeChange(mode) {
    setPlaceOfReceiptMode(mode)
    setValue('placeOfReceiptCountry', '', { shouldValidate: false })
    setValue('placeOfReceiptName', '', { shouldValidate: false })
  }
  function handlePlaceOfDeliveryModeChange(mode) {
    setPlaceOfDeliveryMode(mode)
    setValue('placeOfDeliveryCountry', '', { shouldValidate: false })
    setValue('placeOfDeliveryName', '', { shouldValidate: false })
  }

  return (
    <>
      <CngSection title='Consignment'>
        <CardContent>
          <Grid container spacing={1}>
            <CngGridItem xs={12} sm={6} lg={4}>
              <CodeMaintenanceSearchField required codeType='UNLOC' name='portOfLoading' label={<>Port of Loading{' '}<Tooltip title='Enter the port where the goods are first loaded onto the vessel.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' />
            </CngGridItem>
            <CngGridItem xs={12} sm={6} lg={4}>
              <CodeMaintenanceSearchField required codeType='UNLOC' name='portOfDischarge' label={<>Port of Discharge{' '}<Tooltip title='Enter the port where the goods will be discharged from the vessel. This is the final unloading port if no further transshipment occurs.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' />
            </CngGridItem>
            <CngGridItem xs={12} sm={6} lg={4}>
              <ClearableCngSelectField name='emirate' label={<>Emirate{' '}<Tooltip title='Select the UAE Emirate where the customs declaration will be filed.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' items={EMIRATE} />
            </CngGridItem>
            <CngGridItem xs={12} sm={6} lg={6}>
              <Box>
                <Box display='flex' alignItems='center' style={{ gap: 4, marginBottom: 4 }}>
                  <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 14 }}>Place of Receipt</Typography>
                  <Tooltip title='Enter the location where the carrier first takes possession of the goods for transport.'><span style={{ cursor: 'help' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.5 }} /></span></Tooltip>
                </Box>
                <Box display='flex' style={{ gap: 4, marginBottom: 6 }}>
                  {['unlocode', 'country'].map((mode) => (
                    <Box key={mode} component='button' type='button'
                      onClick={() => !disabled && handlePlaceOfReceiptModeChange(mode)}
                      style={{ padding: '2px 10px', fontSize: 12, fontWeight: placeOfReceiptMode === mode ? 600 : 400, background: placeOfReceiptMode === mode ? '#1a2744' : '#fff', color: placeOfReceiptMode === mode ? '#fff' : 'rgba(0,0,0,0.7)', border: '1px solid rgba(0,0,0,0.23)', borderRadius: 4, cursor: disabled ? 'default' : 'pointer', lineHeight: '20px', whiteSpace: 'nowrap', outline: 'none' }}
                    >{mode === 'unlocode' ? 'UN/LOCODE' : 'Country + location'}</Box>
                  ))}
                </Box>
                {placeOfReceiptMode === 'unlocode' ? (
                  <CodeMaintenanceSearchField codeType='UNLOC' name='placeOfReceiptCountry' label='Search UN/LOCODE...' disabled={disabled} size='small' />
                ) : (
                  <Box display='flex' style={{ gap: 4 }}>
                    <Box style={{ flex: 1, minWidth: 0 }}><SearchableCountryField name='placeOfReceiptCountry' label='Country' disabled={disabled} /></Box>
                    <Box style={{ flex: 2, minWidth: 0 }}><CngTextField name='placeOfReceiptName' label='Location name' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} /></Box>
                  </Box>
                )}
                <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 11 }}>Select a UN/LOCODE, or switch to enter country code + location name</Typography>
              </Box>
            </CngGridItem>
            <CngGridItem xs={12} sm={6} lg={6}>
              <Box>
                <Box display='flex' alignItems='center' style={{ gap: 4, marginBottom: 4 }}>
                  <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 14 }}>Place of Delivery</Typography>
                  <Tooltip title='Enter the final destination where the goods are delivered to the consignee.'><span style={{ cursor: 'help' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.5 }} /></span></Tooltip>
                </Box>
                <Box display='flex' style={{ gap: 4, marginBottom: 6 }}>
                  {['unlocode', 'country'].map((mode) => (
                    <Box key={mode} component='button' type='button'
                      onClick={() => !disabled && handlePlaceOfDeliveryModeChange(mode)}
                      style={{ padding: '2px 10px', fontSize: 12, fontWeight: placeOfDeliveryMode === mode ? 600 : 400, background: placeOfDeliveryMode === mode ? '#1a2744' : '#fff', color: placeOfDeliveryMode === mode ? '#fff' : 'rgba(0,0,0,0.7)', border: '1px solid rgba(0,0,0,0.23)', borderRadius: 4, cursor: disabled ? 'default' : 'pointer', lineHeight: '20px', whiteSpace: 'nowrap', outline: 'none' }}
                    >{mode === 'unlocode' ? 'UN/LOCODE' : 'Country + location'}</Box>
                  ))}
                </Box>
                {placeOfDeliveryMode === 'unlocode' ? (
                  <CodeMaintenanceSearchField codeType='UNLOC' name='placeOfDeliveryCountry' label='Search UN/LOCODE...' disabled={disabled} size='small' />
                ) : (
                  <Box display='flex' style={{ gap: 4 }}>
                    <Box style={{ flex: 1, minWidth: 0 }}><SearchableCountryField name='placeOfDeliveryCountry' label='Country' disabled={disabled} /></Box>
                    <Box style={{ flex: 2, minWidth: 0 }}><CngTextField name='placeOfDeliveryName' label='Location name' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} /></Box>
                  </Box>
                )}
                <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)', fontSize: 11 }}>Select a UN/LOCODE, or switch to enter country code + location name</Typography>
              </Box>
            </CngGridItem>
          </Grid>
        </CardContent>
      </CngSection>

                        {/* Transshipment Ports */}
      <Box mt={2}>
        <Box
          onClick={() => setTransshipOpen(!transshipOpen)}
          display='flex' alignItems='center'
          style={{ padding: '10px 14px', background: '#f5f4f0', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.09)', gap: 8 }}
        >
          <FontAwesomeIcon icon={['fal', 'route']} style={{ fontSize: 14, flexShrink: 0 }} />
          <Typography variant='subtitle2' style={{ flex: 1 }}>Port of Transshipment</Typography>
          <Chip label={isTransshipRequired ? 'Required' : 'Conditional'} size='small' style={{ fontSize: 11, height: 22, ...(isTransshipRequired ? { background: '#d32f2f', color: '#fff' } : {}) }} />
          <Tooltip title='Enter the port(s) where the shipment is transferred from one vessel to another during transit.'><span><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 13, opacity: 0.5 }} /></span></Tooltip>
          <FontAwesomeIcon icon={['fal', transshipOpen ? 'chevron-up' : 'chevron-down']} style={{ fontSize: 13 }} />
        </Box>
        <Collapse in={transshipOpen}>
          <Box mt={1} p={2} border={1} borderColor='divider' borderRadius={8}>
            <Typography variant='caption' color='textSecondary' gutterBottom>Up to 97 transshipment ports per bill.</Typography>
                        {transshipmentPorts.map((port, idx) => (
              <Box key={idx} mb={2} border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
                <Box display='flex' alignItems='center' justifyContent='space-between'
                  style={{ padding: '8px 12px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                  <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                    <FontAwesomeIcon icon={['fal', 'anchor']} style={{ fontSize: 13, opacity: 0.6 }} />
                    <Typography variant='subtitle2'>Transshipment Port #{idx + 1}</Typography>
                  </Box>
                  {!disabled && <IconButton size='small' onClick={() => removeListItem('transshipmentPorts', idx)}><FontAwesomeIcon icon={['fal', 'trash']} /></IconButton>}
                </Box>
                <Box p={2}>
                  <Grid container spacing={1}>
                    <CngGridItem xs={12}><CodeMaintenanceSearchField codeType='UNLOC' value={port.unlocCode || ''} label='UNLOC Code' disabled={disabled} size='small' onChange={(e) => updateField('transshipmentPorts', idx, 'unlocCode', e.target.value)} /></CngGridItem>
                  </Grid>
                </Box>
              </Box>
            ))}
                        {!disabled && transshipmentPorts.length < 97 && (
              <Box mt={1}>
                <CngButton color='primary' size='small' onClick={() => addListItem('transshipmentPorts', { unlocCode: '' })} startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}>
                  Add Transhipment Port
                </CngButton>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

                        {/* Monetary Amount */}
      <Box mt={2}>
        <Box
          onClick={() => setMonetaryOpen(!monetaryOpen)}
          display='flex' alignItems='center'
          style={{ padding: '10px 14px', background: '#f5f4f0', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.09)', gap: 8 }}
        >
          <FontAwesomeIcon icon={['fal', 'dollar-sign']} style={{ fontSize: 14, flexShrink: 0 }} />
          <Typography variant='subtitle2' style={{ flex: 1 }}>Monetary Amount</Typography>
          <Chip label='Optional' size='small' style={{ fontSize: 11, height: 22 }} />
          <Tooltip title='Enter the declared value of the consignment, including freight charges and any other applicable monetary amounts for this bill.'><span><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 13, opacity: 0.5 }} /></span></Tooltip>
          <FontAwesomeIcon icon={['fal', monetaryOpen ? 'chevron-up' : 'chevron-down']} style={{ fontSize: 13 }} />
        </Box>
        <Collapse in={monetaryOpen}>
          <Box mt={1} p={2} border={1} borderColor='divider' borderRadius={8}>
            <Typography variant='caption' color='textSecondary' gutterBottom>Up to 9 monetary amount entries per bill.</Typography>
                        {monetaryDetails.map((item, idx) => (
              <Box key={idx} mb={2} border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
                <Box display='flex' alignItems='center' justifyContent='space-between'
                  style={{ padding: '8px 12px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                  <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                    <FontAwesomeIcon icon={['fal', 'dollar-sign']} style={{ fontSize: 13, opacity: 0.6 }} />
                    <Typography variant='subtitle2'>Monetary Amount #{idx + 1}</Typography>
                  </Box>
                  {!disabled && <IconButton size='small' onClick={() => removeListItem('monetaryDetails', idx)}><FontAwesomeIcon icon={['fal', 'trash']} /></IconButton>}
                </Box>
                <Box p={2}>
                  <Grid container spacing={1}>
                    <CngGridItem xs={12} sm={6} lg={4}><CodeMaintenanceSearchField codeType='MONETARY' value={item.monetaryAmountType || ''} label='Monetary Amount Type' disabled={disabled} size='small' onChange={(e) => updateField('monetaryDetails', idx, 'monetaryAmountType', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}><CngTextField value={item.monetaryAmount ?? ''} label={<>Monetary Amount{' '}<Tooltip title='Enter the amount using digits only. Use a decimal point (.) to separate decimals (e.g., 1250.50).'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' inputProps={{ maxLength: 35 }} error={!!monetaryFieldErrors[idx]?.monetaryAmount} helperText={monetaryFieldErrors[idx]?.monetaryAmount} onChange={(e) => { updateField('monetaryDetails', idx, 'monetaryAmount', e.target.value.replace(/[^0-9.]/g, '')); if (clearMonetaryFieldError) clearMonetaryFieldError(idx, 'monetaryAmount') }} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}><SearchableSelectField value={item.currencyCode || ''} label='Currency Code' disabled={disabled} size='small' items={CURRENCY_CODE} onChange={(e) => updateField('monetaryDetails', idx, 'currencyCode', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={6}><ClearableCngSelectField value={item.chargeCategoryCode || ''} label='Charge Category Code' disabled={disabled} size='small' items={CHARGE_CATEGORY} onChange={(e) => updateField('monetaryDetails', idx, 'chargeCategoryCode', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={6}><ClearableCngSelectField value={item.paymentArrangementCode || ''} label='Payment Arrangement Code' disabled={disabled} size='small' items={PAYMENT_ARRANGEMENT} onChange={(e) => updateField('monetaryDetails', idx, 'paymentArrangementCode', e.target.value)} /></CngGridItem>
                  </Grid>
                </Box>
              </Box>
            ))}
                                                {!disabled && monetaryDetails.length < 9 && (
              <Box mt={1}>
                <CngButton color='primary' size='small' onClick={() => addListItem('monetaryDetails', { monetaryAmountType: '', monetaryAmount: '', currencyCode: '', chargeCategoryCode: '', paymentArrangementCode: '' })} startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}>
                  Add Another Monetary Amount
                </CngButton>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </>
  )
}

const MBL_PARTY_CONFIG = {
  CZ:     { label: 'Shipper (Consignor)',    icon: 'user',      chip: { label: 'Required',    color: '#d32f2f' }, cardBanner: null, partyIdLabel: 'Shipper Id',              partyIdPlaceholder: 'Tax ID',                              partyIdTooltip: "Enter the shipper's Tax Identification Number (Tax ID) as provided on official registration or documentation.",                                showPartyIdType: true,  showEstablishment: false, saveLabel: 'Save shipper to Party Master'   },
  CN:     { label: 'Consignee or Endorsee', icon: 'user-tie',  chip: { label: 'C', color: '#ed6c02' }, cardBanner: "Consignee details depend on the Bill of Lading type:\n\nFor Non-Negotiable Bills: Consignee is required.\nFor Negotiable Bills:\n• Blank Endorsement: Leave consignee empty.\n• To Order of a Bank: Leave consignee empty; provide bank name in 'Payer on behalf of importer'.\n• To Order of Shipper/Third Party: Enter the relevant party as consignee.", partyIdLabel: 'Consignee or Endorsee Id', partyIdPlaceholder: 'TRN / Emirates ID / Country+Passport', partyIdTooltip: 'Enter Consignee Identification based on type:\n• UAE Company → 15-digit TRN\n• UAE Individual → 15-digit Emirates ID (e.g., 784xxxxxxxxxxxx)\n• Non-UAE Individual → Country code + Passport No. (e.g., IN12345678)', showPartyIdType: true, showPartyIdTypeAtBottom: true, showEstablishment: true,  saveLabel: 'Save consignee to Party Master' },
  _other: { label: 'Other Party',           icon: 'user-gear', chip: { label: 'Optional',    color: '#616161' }, partyIdLabel: null,                      partyIdPlaceholder: '',                                    partyIdTooltip: '',                                                                                                                                      showPartyIdType: true,  showEstablishment: false, saveLabel: 'Save party to Party Master',     showPartyType: true }
}
const EMPTY_MBL_PARTY = (type) => ({ partyType: type, _isNewParty: !type, partyIdentificationType: type === 'CZ' ? 'SP' : '', partyId: '', partyName: '', address1: '', address2: '', address3: '', city: '', country: '', establishmentOrIndividual: '', contactInfo: [], savePartyMaster: false })

function Step4Parties({ disabled, setValue, getValues, partiesInfo, addListItem, removeListItem, partyFieldErrors = {}, clearPartyFieldError, processingIndicator }) {
  const isNonNegotiable = processingIndicator === 'N'
  const { watch } = useFormContext()
  const shipmentType = watch('shipmentType')
  const isImport = shipmentType === '23'
    const [contactOpen, setContactOpen] = useState({})

  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerTargetIdx, setPickerTargetIdx] = useState(null)

  useEffect(() => {
    if (!isImport) {
      const list = getValues('partiesInfo') || []
      const filtered = list.filter(p => p.partyType !== 'DP')
      if (filtered.length !== list.length) setValue('partiesInfo', filtered, { shouldValidate: false })
    }
  }, [isImport])

  useEffect(() => {
    const list = getValues('partiesInfo') || []
    const hasCZ = list.some(p => p.partyType === 'CZ')
    const hasCN = list.some(p => p.partyType === 'CN')
    const shells = [...list]
    if (!hasCZ) shells.unshift(EMPTY_MBL_PARTY('CZ'))
    if (!hasCN) {
      const czIdx = shells.findIndex(p => p.partyType === 'CZ')
      shells.splice(czIdx + 1, 0, EMPTY_MBL_PARTY('CN'))
    }
    if (shells.length !== list.length) setValue('partiesInfo', shells.map((p, i) => ({ ...p, _id: i + 1 })), { shouldValidate: false })
  }, [])

  function updatePartyField(index, field, value) {
    const list = [...(getValues('partiesInfo') || [])]
    list[index] = { ...list[index], [field]: value }
    setValue('partiesInfo', list, { shouldValidate: true })
  }

    function addContactInfo(partyIndex) {
    const list = [...(getValues('partiesInfo') || [])]
    const contacts = [...(list[partyIndex].contactInfo || []), { contactLevel: '', contactName: '', telephoneNumber: '', emailId: '', url: '' }]
    list[partyIndex] = { ...list[partyIndex], contactInfo: contacts }
    setValue('partiesInfo', list, { shouldValidate: true })
  }
  function removeContactInfo(partyIndex, contactIndex) {
    const list = [...(getValues('partiesInfo') || [])]
    const contacts = [...(list[partyIndex].contactInfo || [])]
    contacts.splice(contactIndex, 1)
    list[partyIndex] = { ...list[partyIndex], contactInfo: contacts }
    setValue('partiesInfo', list, { shouldValidate: true })
  }
  function updateContactInfo(partyIndex, contactIndex, field, value) {
    const list = [...(getValues('partiesInfo') || [])]
    const contacts = [...(list[partyIndex].contactInfo || [])]
    contacts[contactIndex] = { ...contacts[contactIndex], [field]: value }
    list[partyIndex] = { ...list[partyIndex], contactInfo: contacts }
    setValue('partiesInfo', list, { shouldValidate: true })
  }

    function openPicker(idx) {
    setPickerTargetIdx(idx)
    setPickerOpen(true)
  }

  function handlePickerSelect(party) {
    if (pickerTargetIdx === null) return
    const list = [...(getValues('partiesInfo') || [])]
    const currentParty = list[pickerTargetIdx] || {}
    list[pickerTargetIdx] = {
      ...currentParty,
      partyIdentificationType: party.partyIdentificationType || currentParty.partyIdentificationType || '',
      partyId: party.partyId || '',
      partyName: party.partyName || '',
      address1: party.street || party.address1 || '',
      address2: party.streetNumber || party.address2 || '',
      address3: party.postalCode || party.address3 || '',
      city: party.city || '',
      country: party.country || '',
      establishmentOrIndividual: party.establishmentOrIndividual || '',
      contactInfo: (party.contactInfo || currentParty.contactInfo || []).map(
        ({ contactLevel, contactName, telephoneNumber, emailId, url }) =>
          ({ contactLevel, contactName, telephoneNumber, emailId, url })
      )
    }
    setValue('partiesInfo', list, { shouldValidate: true })
  }

  const mblFixedTypes = ['CZ', 'CN']
  const fixedParties = partiesInfo.filter(p => p.partyType && mblFixedTypes.includes(p.partyType))
  const otherParties = partiesInfo.filter(p => p._isNewParty || (p.partyType && !mblFixedTypes.includes(p.partyType)))
  const orderedParties = [...['CZ','CN'].map(t => fixedParties.find(p => p.partyType === t)).filter(Boolean), ...otherParties]

    return (
    <>
    <CngSection title='Parties Information'>
      <CardContent>
        {orderedParties.map((party) => {
          const idx = partiesInfo.indexOf(party)
          const cfg = MBL_PARTY_CONFIG[party.partyType] || MBL_PARTY_CONFIG._other
          const isFixed = mblFixedTypes.includes(party.partyType)
          const contactTouched = (party.contactInfo || []).length > 0
          const contactComplete = contactTouched && (party.contactInfo || []).every(c => c.contactLevel && c.contactName?.trim())
          return (
          <Box key={idx} mb={2} border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
            {/* Card header */}
            <Box display='flex' alignItems='center' justifyContent='space-between'
              style={{ padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
              <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                <FontAwesomeIcon icon={['fal', (MBL_PARTY_CONFIG[party.partyType] || MBL_PARTY_CONFIG._other).icon]} style={{ fontSize: 14, opacity: 0.7 }} />
                <Typography variant='subtitle2'>{(MBL_PARTY_CONFIG[party.partyType] || MBL_PARTY_CONFIG._other).label}</Typography>
                <Chip label={cfg.chip.label} size='small'
                  style={{ fontSize: cfg.chip.label.length === 1 ? 11 : 11, height: 20, minWidth: cfg.chip.label.length === 1 ? 20 : undefined, borderRadius: cfg.chip.label.length === 1 ? '50%' : 4, color: '#fff', background: cfg.chip.color }} />
                {cfg.cardBanner && (
                  <Tooltip title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{cfg.cardBanner}</span>}>
                    <span style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 14, opacity: 0.6 }} /></span>
                  </Tooltip>
                )}
              </Box>
                            {!disabled && (
                <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                  <CngButton color='secondary' size='small' variant='outlined' onClick={() => openPicker(idx)} startIcon={<FontAwesomeIcon icon={['fal', 'address-book']} />}>Pick from Party Master</CngButton>
                  {!mblFixedTypes.includes(party.partyType) && (
                    <IconButton size='small' onClick={() => removeListItem('partiesInfo', idx)}><FontAwesomeIcon icon={['fal', 'trash']} /></IconButton>
                  )}
                </Box>
              )}
            </Box>
            <Box p={2}>
            <Grid container spacing={1}>
              {cfg.showPartyType && (
                <CngGridItem xs={12} sm={6} lg={4}>
                  <ClearableCngSelectField required value={party.partyType || ''} label='Party Type' disabled={disabled} size='small' items={PARTY_TYPE.filter(o => !['CZ', 'CN'].includes(o.value) && (o.value !== 'DP' || isImport))} error={!!partyFieldErrors[idx]?.partyType} helperText={partyFieldErrors[idx]?.partyType} onChange={(e) => { updatePartyField(idx, 'partyType', e.target.value); if (clearPartyFieldError) clearPartyFieldError(idx, 'partyType') }} />
                </CngGridItem>
              )}
              {cfg.showPartyIdType && !cfg.showPartyIdTypeAtBottom && (
                <CngGridItem xs={12} sm={6} lg={4}>
                  <ClearableCngSelectField required={!!party.partyType} value={party.partyIdentificationType || ''} label='Party Identification Type' disabled={disabled} size='small' items={PARTY_ID_TYPE} error={!!partyFieldErrors[idx]?.partyIdentificationType} helperText={partyFieldErrors[idx]?.partyIdentificationType} onChange={(e) => { updatePartyField(idx, 'partyIdentificationType', e.target.value); if (clearPartyFieldError) clearPartyFieldError(idx, 'partyIdentificationType') }} />
                </CngGridItem>
              )}
              {cfg.partyIdLabel && (
                <CngGridItem xs={12} sm={6} lg={4}>
                  <CngTextField required={!!cfg.partyIdRequired || (party.partyType === 'CN' && !!party.establishmentOrIndividual)} value={party.partyId || ''} label={cfg.partyIdTooltip ? <>{cfg.partyIdLabel} <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{cfg.partyIdTooltip}</span>}><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></> : cfg.partyIdLabel} placeholder={cfg.partyIdPlaceholder} disabled={disabled} size='small' inputProps={{ maxLength: 35 }} error={!!partyFieldErrors[idx]?.partyId} helperText={partyFieldErrors[idx]?.partyId} onChange={(e) => { updatePartyField(idx, 'partyId', e.target.value.toUpperCase()); if (clearPartyFieldError) clearPartyFieldError(idx, 'partyId') }} />
                </CngGridItem>
              )}
              <CngGridItem xs={12} sm={6} lg={4}><CngTextField required={party.partyType !== 'CN' || isNonNegotiable} value={party.partyName || ''} label='Party name' disabled={disabled} size='small' inputProps={{ maxLength: 70 }} error={!!partyFieldErrors[idx]?.partyName} helperText={partyFieldErrors[idx]?.partyName} onChange={(e) => { updatePartyField(idx, 'partyName', e.target.value.toUpperCase()); if (clearPartyFieldError) clearPartyFieldError(idx, 'partyName') }} /></CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}><CngTextField required={isFixed && (party.partyType !== 'CN' || isNonNegotiable)} value={party.address1 || ''} label='Address1' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} error={!!partyFieldErrors[idx]?.address1} helperText={partyFieldErrors[idx]?.address1} onChange={(e) => { updatePartyField(idx, 'address1', e.target.value.toUpperCase()); if (clearPartyFieldError) clearPartyFieldError(idx, 'address1') }} /></CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}><CngTextField value={party.address2 || ''} label='Address2' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} onChange={(e) => updatePartyField(idx, 'address2', e.target.value.toUpperCase())} /></CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}><CngTextField value={party.address3 || ''} label='Address3' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} onChange={(e) => updatePartyField(idx, 'address3', e.target.value.toUpperCase())} /></CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}><CngTextField required={isFixed && (party.partyType !== 'CN' || isNonNegotiable)} value={party.city || ''} label='City' disabled={disabled} size='small' inputProps={{ maxLength: 35 }} error={!!partyFieldErrors[idx]?.city} helperText={partyFieldErrors[idx]?.city} onChange={(e) => { updatePartyField(idx, 'city', e.target.value.toUpperCase()); if (clearPartyFieldError) clearPartyFieldError(idx, 'city') }} /></CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}><SearchableCountryField required={isFixed && (party.partyType !== 'CN' || isNonNegotiable)} value={party.country || ''} label='Country' disabled={disabled} error={!!partyFieldErrors[idx]?.country} helperText={partyFieldErrors[idx]?.country} onChange={(e) => { updatePartyField(idx, 'country', e.target.value); if (clearPartyFieldError) clearPartyFieldError(idx, 'country') }} /></CngGridItem>
              {cfg.showEstablishment && (
                <CngGridItem xs={12} sm={6} lg={4}><ClearableCngSelectField required={party.partyType === 'CN' && !!party.partyId} value={party.establishmentOrIndividual || ''} label='Establishment or Individual' disabled={disabled} size='small' items={ESTABLISHMENT_OR_INDIVIDUAL} error={!!partyFieldErrors[idx]?.establishmentOrIndividual} helperText={partyFieldErrors[idx]?.establishmentOrIndividual} onChange={(e) => { updatePartyField(idx, 'establishmentOrIndividual', e.target.value); if (clearPartyFieldError) clearPartyFieldError(idx, 'establishmentOrIndividual') }} /></CngGridItem>
              )}
              {cfg.showPartyIdType && cfg.showPartyIdTypeAtBottom && (
                <CngGridItem xs={12} sm={6} lg={4}>
                  <ClearableCngSelectField required={party.partyType === 'CN' && isNonNegotiable} value={party.partyIdentificationType || ''} label='Party Identification Type' disabled={disabled} size='small' items={PARTY_ID_TYPE} error={!!partyFieldErrors[idx]?.partyIdentificationType} helperText={partyFieldErrors[idx]?.partyIdentificationType} onChange={(e) => { updatePartyField(idx, 'partyIdentificationType', e.target.value); if (clearPartyFieldError) clearPartyFieldError(idx, 'partyIdentificationType') }} />
                </CngGridItem>
              )}
            </Grid>
            {/* Contact Information collapsible */}
            <Box mt={2}>
              <Box onClick={() => setContactOpen(p => ({ ...p, [idx]: !p[idx] }))} display='flex' alignItems='center'
                style={{ padding: '8px 12px', background: '#f5f4f0', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.09)', gap: 8 }}>
                <FontAwesomeIcon icon={['fal', 'address-card']} style={{ fontSize: 13, flexShrink: 0 }} />
                <Typography variant='subtitle2' style={{ flex: 1, fontSize: 13 }}>Contact Information</Typography>
                <Chip
                  label={contactComplete ? 'Completed' : contactTouched ? 'In Progress' : 'Optional'}
                  size='small'
                  style={{
                    fontSize: 11,
                    height: 20,
                    ...(contactComplete ? { background: '#2e7d32', color: '#fff' } : contactTouched ? { background: '#ed6c02', color: '#fff' } : {})
                  }}
                />
                <FontAwesomeIcon icon={['fal', contactOpen[idx] ? 'chevron-up' : 'chevron-down']} style={{ fontSize: 12 }} />
              </Box>
              <Collapse in={!!contactOpen[idx]}>
                <Box mt={1}>
                  {contactTouched && !contactComplete && (
                    <Box mb={1} display='flex' alignItems='flex-start' style={{ gap: 10, padding: '8px 12px', background: '#fff8e8', border: '1px solid #f59e0b', borderRadius: 8 }}>
                      <FontAwesomeIcon icon={['fal', 'circle-exclamation']} style={{ fontSize: 13, marginTop: 2, flexShrink: 0, color: '#b45309' }} />
                      <Typography variant='caption' style={{ color: '#7a4a00' }}>Contact Level and Contact Name are required for each contact added.</Typography>
                    </Box>
                  )}
                  {(party.contactInfo || []).map((contact, cIdx) => (
                    <Box key={cIdx} mb={1} p={1} border={1} borderColor='divider' borderRadius={4}>
                      <Grid container spacing={1} alignItems='center'>
                        <CngGridItem xs={12} sm={5} lg={4}><ClearableCngSelectField required value={contact.contactLevel || ''} label='Contact Level' disabled={disabled} size='small' items={CONTACT_LEVEL} error={!!partyFieldErrors[idx]?.contactInfo?.[cIdx]?.contactLevel} helperText={partyFieldErrors[idx]?.contactInfo?.[cIdx]?.contactLevel} onChange={(e) => updateContactInfo(idx, cIdx, 'contactLevel', e.target.value)} /></CngGridItem>
                        <CngGridItem xs={12} sm={6} lg={7}><CngTextField required value={contact.contactName || ''} label='Contact Name' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} error={!!partyFieldErrors[idx]?.contactInfo?.[cIdx]?.contactName} helperText={partyFieldErrors[idx]?.contactInfo?.[cIdx]?.contactName} onChange={(e) => updateContactInfo(idx, cIdx, 'contactName', e.target.value)} /></CngGridItem>
                        {!disabled && <CngGridItem xs={12} sm={1} lg={1}><Box display='flex' alignItems='center' justifyContent='center' height='100%'><IconButton size='small' onClick={() => removeContactInfo(idx, cIdx)}><FontAwesomeIcon icon={['fal', 'trash']} /></IconButton></Box></CngGridItem>}
                      </Grid>
                      <Grid container spacing={1} alignItems='center' style={{ marginTop: 4 }}>
                        <CngGridItem xs={12} sm={4} lg={4}><CngTextField value={contact.telephoneNumber || ''} label='Telephone Number' disabled={disabled} size='small' inputProps={{ maxLength: 512 }} onChange={(e) => updateContactInfo(idx, cIdx, 'telephoneNumber', e.target.value)} /></CngGridItem>
                        <CngGridItem xs={12} sm={4} lg={4}><CngTextField value={contact.emailId || ''} label='Email ID' disabled={disabled} size='small' inputProps={{ maxLength: 512 }} onChange={(e) => updateContactInfo(idx, cIdx, 'emailId', e.target.value)} /></CngGridItem>
                        <CngGridItem xs={12} sm={4} lg={4}><CngTextField value={contact.url || ''} label='URL' disabled={disabled} size='small' inputProps={{ maxLength: 512 }} onChange={(e) => updateContactInfo(idx, cIdx, 'url', e.target.value)} /></CngGridItem>
                      </Grid>
                    </Box>
                  ))}
                  {!disabled && <Box mt={1}><CngButton color='default' size='small' onClick={() => addContactInfo(idx)} disabled={(party.contactInfo || []).length >= 9} startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}>Add Contact</CngButton></Box>}
                </Box>
              </Collapse>
            </Box>
                                    {/* Save to Party Master toggle */}
            {!disabled && (
              <Box mt={2} display='flex' alignItems='center'
                style={{ padding: '8px 12px', background: '#fafafa', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 8, gap: 8 }}>
                <FontAwesomeIcon icon={['fal', 'bookmark']} style={{ fontSize: 13, opacity: 0.5 }} />
                <Typography variant='caption' style={{ flex: 1 }}>{cfg.saveLabel} — reuse on future filings</Typography>
                <Switch size='small' checked={!!party.savePartyMaster} onChange={(e) => updatePartyField(idx, 'savePartyMaster', e.target.checked)} />
              </Box>
            )}
            </Box>{/* end card padding */}
          </Box>
        )})
        }
        {isImport && (
          <Box mb={2} display='flex' alignItems='flex-start' style={{ gap: 10, padding: '10px 14px', background: '#e8f4fd', border: '1px solid #b3d9f7', borderRadius: 8 }}>
            <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 14, marginTop: 2, flexShrink: 0, color: '#0c447c' }} />
            <Typography variant='caption' style={{ color: '#0c447c' }}>Delivery Agent is mandatory if Shipment Type is 'Import shipments' and delivery agent is not the local UAE Freight Forwarder. Delivery Agent does not apply to other movement types than import.</Typography>
          </Box>
        )}

        {!disabled && (
          <Box mt={1}>
            <CngButton color='primary' size='small' disabled={otherParties.length >= 6} onClick={() => addListItem('partiesInfo', EMPTY_MBL_PARTY(''))} startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}>Add Another Party (up to 6 total)</CngButton>
          </Box>
        )}
      </CardContent>
    </CngSection>

        <PartyMasterPickerDialog
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      onSelect={handlePickerSelect}
    />
    </>
  )
}

function Step5Containers({ disabled, setValue, getValues, containerDetails, addListItem, removeListItem, containerFieldErrors = {}, clearContainerFieldError }) {
  const [additionalOpen, setAdditionalOpen] = useState(() => {
    const init = {}
    ;(containerDetails || []).forEach((c, i) => {
      if (c.containerStatusDescription || c.serviceRequirementCode || c.cargoType || c.sealingPartyCode || c.sealingPartyName || c.sealType) init[i] = true
    })
    return init
  })

  useEffect(() => {
    const list = getValues('containerDetails') || []
    Object.keys(containerFieldErrors).forEach(i => {
      if (containerFieldErrors[i]?.temperatureMeasurementCode && !list[i]?.reeferOn) {
        updateContainerField(Number(i), 'reeferOn', true)
      }
    })
  }, [containerFieldErrors])

  function updateContainerField(index, field, value) {
    const list = [...(getValues('containerDetails') || [])]
    list[index] = { ...list[index], [field]: value }
    setValue('containerDetails', list, { shouldValidate: true })
  }

  const isReefer = (ctr) => ['22R1', '42R1', '45R1'].includes(ctr.containerSize)

  return (
    <CngSection title='Container Details'>
      <CardContent>
        <Box mb={2} display='flex' alignItems='flex-start' style={{ gap: 10, padding: '10px 14px', background: '#e8f4fd', border: '1px solid #b3d9f7', borderRadius: 8 }}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 14, marginTop: 2, flexShrink: 0, color: '#0c447c' }} />
          <Typography variant='caption' style={{ color: '#0c447c' }}>
            Maximum 999 containers per master bill. Container number follows BIC ISO 6346 standard and cannot be repeated across this bill. Each container has its own <strong>Additional Container Info</strong>. Toggle <strong>Refrigerated</strong> to reveal temperature fields.
          </Typography>
        </Box>
        {containerDetails.map((ctr, idx) => (
          <Box key={idx} mb={2} border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
            {/* Card header */}
            <Box display='flex' alignItems='center' justifyContent='space-between'
              style={{ padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
              <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                <FontAwesomeIcon icon={['fal', 'container-storage']} style={{ fontSize: 14, opacity: 0.7 }} />
                <Typography variant='subtitle2'>Container #{idx + 1}</Typography>
              </Box>
                                                        {!disabled && (
                <CngButton color='secondary' size='small' variant='outlined' onClick={() => removeListItem('containerDetails', idx)} startIcon={<FontAwesomeIcon icon={['fal', 'trash']} style={{ fontSize: 13, color: '#d32f2f' }} />}>
                  Remove
                </CngButton>
              )}
            </Box>
            <Box p={2}>
              {/* Main fields row */}
              <Grid container spacing={1}>
                <CngGridItem xs={12} sm={4} lg={4}>
                  <CngTextField
                    required
                    value={ctr.containerNumber || ''}
                    label={<>Container Number{' '}<Tooltip title='Provide the container number in ISO 6346 format. The same container number cannot be repeated within this bill.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>}
                    disabled={disabled}
                    size='small'
                    inputProps={{ maxLength: 17 }}
                    error={!!containerFieldErrors[idx]?.containerNumber}
                    helperText={containerFieldErrors[idx]?.containerNumber}
                    onChange={(e) => { updateContainerField(idx, 'containerNumber', e.target.value.toUpperCase()); if (clearContainerFieldError) clearContainerFieldError(idx, 'containerNumber') }}
                  />
                </CngGridItem>
                <CngGridItem xs={12} sm={4} lg={4}>
                  <ClearableCngSelectField
                    required
                    value={ctr.containerSize || ''}
                    label={<>Container Size{' '}<Tooltip title='Select the container type and size as per the BIC ISO 6346 standard.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>}
                    disabled={disabled}
                    size='small'
                    helperText={containerFieldErrors[idx]?.containerSize}
                    error={!!containerFieldErrors[idx]?.containerSize}
                    items={CONTAINER_SIZE}
                    onChange={(e) => {
                      updateContainerField(idx, 'containerSize', e.target.value)
                      if (['22R1', '42R1', '45R1'].includes(e.target.value)) updateContainerField(idx, 'reeferOn', true)
                      if (clearContainerFieldError) clearContainerFieldError(idx, 'containerSize')
                    }}
                  />
                </CngGridItem>
                <CngGridItem xs={12} sm={4} lg={4}>
                  <ClearableCngSelectField
                    required
                    value={ctr.containerLoadStatus || ''}
                    label='Container Load Status'
                    disabled={disabled}
                    size='small'
                    helperText={containerFieldErrors[idx]?.containerLoadStatus}
                    error={!!containerFieldErrors[idx]?.containerLoadStatus}
                    items={CONTAINER_LOAD_STATUS}
                    onChange={(e) => { updateContainerField(idx, 'containerLoadStatus', e.target.value); if (clearContainerFieldError) clearContainerFieldError(idx, 'containerLoadStatus') }}
                  />
                </CngGridItem>
                <CngGridItem xs={12} sm={4} lg={4}>
                  <CngTextField
                    required
                    value={ctr.sealIdentifier || ''}
                    label='Seal Identifier'
                    disabled={disabled}
                    size='small'
                    inputProps={{ maxLength: 35 }}
                    error={!!containerFieldErrors[idx]?.sealIdentifier}
                    helperText={containerFieldErrors[idx]?.sealIdentifier}
                    onChange={(e) => { updateContainerField(idx, 'sealIdentifier', e.target.value.toUpperCase()); if (clearContainerFieldError) clearContainerFieldError(idx, 'sealIdentifier') }}
                  />
                </CngGridItem>
                <CngGridItem xs={12} sm={4} lg={4}>
                  <CngTextField
                    value={ctr.containerVerifiedGrossMass ?? ''}
                    label='Container Verified Gross Mass'
                    disabled={disabled}
                    size='small'
                    inputProps={{ maxLength: 18 }}
                    placeholder='0'
                    error={!!containerFieldErrors[idx]?.containerVerifiedGrossMass}
                    helperText={containerFieldErrors[idx]?.containerVerifiedGrossMass}
                    onChange={(e) => { updateContainerField(idx, 'containerVerifiedGrossMass', e.target.value.replace(/[^0-9]/g, '')); if (clearContainerFieldError) clearContainerFieldError(idx, 'containerVerifiedGrossMass') }}
                  />
                </CngGridItem>
                <CngGridItem xs={12} sm={4} lg={4}>
                  <ClearableCngSelectField
                    required={!!ctr.containerVerifiedGrossMass}
                    value={ctr.containerVerifiedGrossMassUnit || ''}
                    label='Container Verified Gross Mass Unit'
                    disabled={disabled}
                    size='small'
                    error={!!containerFieldErrors[idx]?.containerVerifiedGrossMassUnit}
                    helperText={containerFieldErrors[idx]?.containerVerifiedGrossMassUnit}
                    items={VGM_UNIT}
                    onChange={(e) => { updateContainerField(idx, 'containerVerifiedGrossMassUnit', e.target.value); if (clearContainerFieldError) clearContainerFieldError(idx, 'containerVerifiedGrossMassUnit') }}
                  />
                </CngGridItem>
              </Grid>

              {/* Refrigerated toggle row */}
              <Box mt={2} display='flex' alignItems='center'
                style={{ padding: '10px 14px', background: ctr.reeferOn ? '#fff8e8' : '#f5f4f0', border: '1px solid', borderColor: ctr.reeferOn ? '#f59e0b' : 'rgba(0,0,0,0.09)', borderRadius: 8, gap: 10 }}>
                <FontAwesomeIcon icon={['fal', 'snowflake']} style={{ fontSize: 14, flexShrink: 0, color: ctr.reeferOn ? '#0c447c' : 'inherit' }} />
                <Typography variant='subtitle2' style={{ flex: 1 }}>
                  Reefer
                </Typography>
                <Typography variant='caption' color='textSecondary' style={{ marginRight: 8 }}>— show temperature fields</Typography>
                <Switch
                  size='small'
                  checked={!!ctr.reeferOn}
                  onChange={(e) => {
                    const checked = e.target.checked
                    const list = [...(getValues('containerDetails') || [])]
                    list[idx] = checked
                      ? { ...list[idx], reeferOn: true }
                      : { ...list[idx], reeferOn: false, temperatureType: null, containerTemperatureDegree: null, temperatureMeasurementCode: null }
                    setValue('containerDetails', list, { shouldValidate: true })
                  }}
                  color='primary'
                />
              </Box>

              {/* Refrigerated Container Info — shown when toggle ON */}
              {ctr.reeferOn && (
                <Box mt={1} p={2} style={{ background: '#fffdf5', border: '1px solid #ffe082', borderRadius: 8 }}>
                  <Box display='flex' alignItems='center' style={{ gap: 8, marginBottom: 12 }}>
                    <FontAwesomeIcon icon={['fal', 'snowflake']} style={{ fontSize: 13, color: '#0c447c' }} />
                    <Typography variant='subtitle2' style={{ fontSize: 13 }}>Refrigerated Container Info</Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <CngGridItem xs={12} sm={4} lg={4}>
                      <ClearableCngSelectField
                        value={ctr.temperatureType || ''}
                        label='Temperature Type'
                        disabled={disabled}
                        size='small'
                        items={TEMPERATURE_TYPE}
                        onChange={(e) => updateContainerField(idx, 'temperatureType', e.target.value)}
                      />
                    </CngGridItem>
                    <CngGridItem xs={12} sm={4} lg={4}>
                      <CngTextField
                        value={ctr.containerTemperatureDegree ?? ''}
                        label='Container Temperature degree'
                        disabled={disabled}
                        size='small'
                        inputProps={{ maxLength: 15 }}
                        placeholder='e.g., -18'
                        error={!!containerFieldErrors[idx]?.containerTemperatureDegree}
                        helperText={containerFieldErrors[idx]?.containerTemperatureDegree}
                        onChange={(e) => { updateContainerField(idx, 'containerTemperatureDegree', e.target.value.replace(/[^0-9-]/g, '')); if (clearContainerFieldError) clearContainerFieldError(idx, 'containerTemperatureDegree') }}
                      />
                    </CngGridItem>
                    <CngGridItem xs={12} sm={4} lg={4}>
                      <ClearableCngSelectField
                        required={!!ctr.containerTemperatureDegree}
                        value={ctr.temperatureMeasurementCode || ''}
                        label='Temperature Measurement Code'
                        disabled={disabled}
                        size='small'
                        error={!!containerFieldErrors[idx]?.temperatureMeasurementCode}
                        helperText={containerFieldErrors[idx]?.temperatureMeasurementCode}
                        items={CONTAINER_TEMPERATURE_UNIT}
                        onChange={(e) => { updateContainerField(idx, 'temperatureMeasurementCode', e.target.value); if (clearContainerFieldError) clearContainerFieldError(idx, 'temperatureMeasurementCode') }}
                      />
                    </CngGridItem>
                  </Grid>
                </Box>
              )}

              {/* Additional Container Info collapsible */}
              <Box mt={2}>
                <Box
                  onClick={() => setAdditionalOpen(p => ({ ...p, [idx]: !p[idx] }))}
                  display='flex' alignItems='center'
                  style={{ padding: '8px 14px', background: '#f5f4f0', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.09)', gap: 8 }}
                >
                  <FontAwesomeIcon icon={['fal', 'plus']} style={{ fontSize: 12, flexShrink: 0 }} />
                  <Typography variant='subtitle2' style={{ flex: 1, fontSize: 13 }}>Additional Container Info</Typography>
                  <Chip label='Optional' size='small' style={{ fontSize: 11, height: 20 }} />
                  <FontAwesomeIcon icon={['fal', additionalOpen[idx] ? 'chevron-up' : 'chevron-down']} style={{ fontSize: 12 }} />
                </Box>
                <Collapse in={!!additionalOpen[idx]}>
                  <Box mt={1} p={2} border={1} borderColor='divider' borderRadius={8}>
                    <Grid container spacing={1}>
                      <CngGridItem xs={12} sm={4} lg={4}>
                        <CngTextField
                                                    value={ctr.containerStatusDescription || ''}
                          label='Container Status Description'
                          disabled={disabled}
                          size='small'
                          inputProps={{ maxLength: 35 }}
                          onChange={(e) => updateContainerField(idx, 'containerStatusDescription', e.target.value)}
                        />
                      </CngGridItem>
                      <CngGridItem xs={12} sm={4} lg={4}>
                        <SearchableSelectField
                          value={ctr.serviceRequirementCode || ''}
                          label='Service Requirement Code'
                          disabled={disabled}
                          items={SERVICE_REQUIREMENT}
                          onChange={(e) => updateContainerField(idx, 'serviceRequirementCode', e.target.value)}
                        />
                      </CngGridItem>
                      <CngGridItem xs={12} sm={4} lg={4}>
                        <ClearableCngSelectField
                          value={ctr.cargoType || ''}
                          label='Cargo Type'
                          disabled={disabled}
                          size='small'
                          items={CARGO_TYPE}
                          onChange={(e) => updateContainerField(idx, 'cargoType', e.target.value)}
                        />
                      </CngGridItem>
                      <CngGridItem xs={12} sm={4} lg={4}>
                        <ClearableCngSelectField
                          value={ctr.sealingPartyCode || ''}
                          label='Sealing Party Code'
                          disabled={disabled}
                          size='small'
                          items={SEALING_PARTY}
                          onChange={(e) => updateContainerField(idx, 'sealingPartyCode', e.target.value)}
                        />
                      </CngGridItem>
                      <CngGridItem xs={12} sm={4} lg={4}>
                        <CngTextField
                          value={ctr.sealingPartyName || ''}
                          label='Sealing Party Name'
                          disabled={disabled}
                          size='small'
                          inputProps={{ maxLength: 35 }}
                          onChange={(e) => updateContainerField(idx, 'sealingPartyName', e.target.value)}
                        />
                      </CngGridItem>
                      <CngGridItem xs={12} sm={4} lg={4}>
                        <ClearableCngSelectField
                          value={ctr.sealType || ''}
                          label='Seal Type'
                          disabled={disabled}
                          size='small'
                          items={SEAL_TYPE}
                          onChange={(e) => updateContainerField(idx, 'sealType', e.target.value)}
                        />
                      </CngGridItem>
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>
        ))}
                {!disabled && containerDetails.length < 999 && (
          <CngButton color='primary' size='small' onClick={() => addListItem('containerDetails', {
            containerNumber: '', containerSize: '', containerLoadStatus: '',
            sealIdentifier: '', containerVerifiedGrossMass: '', containerVerifiedGrossMassUnit: '',
            reeferOn: false, temperatureType: '', containerTemperatureDegree: '', temperatureMeasurementCode: '',
            containerStatusDescription: '', serviceRequirementCode: '', cargoType: '',
            sealingPartyCode: '', sealingPartyName: '', sealType: ''
          })} startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}>
            Add Container
          </CngButton>
        )}
      </CardContent>
    </CngSection>
  )
}

function Step6Goods({ disabled, setValue, getValues, goodsInfo, containerDetails, addListItem, removeListItem, goodsFieldErrors = {}, clearGoodsFieldError, clearDgFieldError }) {
  const [additionalOpen, setAdditionalOpen] = useState(() => {
    const init = {}
    ;(goodsInfo || []).forEach((g, i) => {
      if (g.additionalInformationRelatedToGoodsItem || g.goodsItemNetWeight != null || g.volume != null || g.packingInstructionsCode || g.marksAndLabels || g.locationName || g.handlingInstructionDescriptionCode || g.handlingInstructionDescription) init[i] = true
    })
    return init
  })
  const [dgOpen, setDgOpen] = useState(() => {
    const init = {}
    ;(goodsInfo || []).forEach((g, i) => {
      if ((g.dangerousGoodsInfo || []).length > 0) init[i] = true
    })
    return init
  })

  const is705 = getValues('documentType') === '705'

  const containerOptions = (containerDetails || [])
    .filter(c => c.containerNumber)
    .map(c => ({ text: c.containerNumber, value: c.containerNumber }))

  function updateGoodsField(index, field, value) {
    const list = [...(getValues('goodsInfo') || [])]
    list[index] = { ...list[index], [field]: value }
    setValue('goodsInfo', list, { shouldValidate: true })
  }

  function addDgInfo(goodsIndex) {
    const list = [...(getValues('goodsInfo') || [])]
    const dg = [...(list[goodsIndex].dangerousGoodsInfo || []), {
      dangerousGoodsHazardClassCode: '', unitedNationsDangerousGoodsIdentifier: '',
      dangerousGoodsFlashpointDescription: '', shipmentFlashpointDegree: '',
      flashpointUnitCode: '', packagingDangerLevelCode: ''
    }]
    list[goodsIndex] = { ...list[goodsIndex], dangerousGoodsInfo: dg }
    setValue('goodsInfo', list, { shouldValidate: true })
  }

  function removeDgInfo(goodsIndex, dgIndex) {
    const list = [...(getValues('goodsInfo') || [])]
    const dg = [...(list[goodsIndex].dangerousGoodsInfo || [])]
    dg.splice(dgIndex, 1)
    list[goodsIndex] = { ...list[goodsIndex], dangerousGoodsInfo: dg }
    setValue('goodsInfo', list, { shouldValidate: true })
  }

  function updateDgField(goodsIndex, dgIndex, field, value) {
    const list = [...(getValues('goodsInfo') || [])]
    const dg = [...(list[goodsIndex].dangerousGoodsInfo || [])]
    dg[dgIndex] = { ...dg[dgIndex], [field]: value }
    list[goodsIndex] = { ...list[goodsIndex], dangerousGoodsInfo: dg }
    setValue('goodsInfo', list, { shouldValidate: true })
  }

  return (
    <CngSection title='Goods Information'>
      <CardContent>
        <Box mb={1}>
          <Typography variant='body2' color='textSecondary'>HS Code mandatory for Type 705 (non-consolidation). Each goods item tied to a container.</Typography>
        </Box>
                {goodsInfo.map((item, idx) => (
          <Box key={idx} mb={2} border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
            <Box display='flex' alignItems='center' justifyContent='space-between'
              style={{ padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
              <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                <FontAwesomeIcon icon={['fal', 'box']} style={{ fontSize: 14, opacity: 0.7 }} />
                <Typography variant='subtitle2'>Goods Item #{String(idx + 1).padStart(6, '0')}</Typography>
              </Box>
              {!disabled && <CngButton color='secondary' size='small' variant='outlined' onClick={() => removeListItem('goodsInfo', idx)} startIcon={<FontAwesomeIcon icon={['fal', 'trash']} />}>Remove Item</CngButton>}
            </Box>
                        <Box p={2}>
              <Grid container spacing={1}>
                                <CngGridItem xs={12} sm={6} lg={4}>
                  <SearchableSelectField required value={item.containerNumber || ''} label='Container Number' disabled={disabled} size='small' items={containerOptions} error={!!goodsFieldErrors[idx]?.containerNumber} helperText={goodsFieldErrors[idx]?.containerNumber} onChange={(e) => { updateGoodsField(idx, 'containerNumber', e.target.value); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'containerNumber') }} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField value={item.goodsItemNumber || ''} label='Goods Item Number' disabled={true} size='small' />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField required value={item.totalPackageQuantity ?? ''} label='Total Package Quantity' disabled={disabled} size='small' inputProps={{ maxLength: 8 }} error={!!goodsFieldErrors[idx]?.totalPackageQuantity} helperText={goodsFieldErrors[idx]?.totalPackageQuantity} onChange={(e) => { updateGoodsField(idx, 'totalPackageQuantity', e.target.value.replace(/[^0-9]/g, '')); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'totalPackageQuantity') }} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField value={item.packageType || ''} label='Package Type' disabled={disabled} size='small' inputProps={{ maxLength: 35 }} onChange={(e) => updateGoodsField(idx, 'packageType', e.target.value)} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <SearchableSelectField required value={item.packageTypeDescription || ''} label='Package Type Description' items={PACKAGE_TYPE} disabled={disabled} error={!!goodsFieldErrors[idx]?.packageTypeDescription} helperText={goodsFieldErrors[idx]?.packageTypeDescription} onChange={(e) => { updateGoodsField(idx, 'packageTypeDescription', e.target.value); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'packageTypeDescription') }} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <ClearableCngSelectField value={item.packingDescription || ''} label='Packing Description' disabled={disabled} size='small' items={PACKING_DESCRIPTION} onChange={(e) => updateGoodsField(idx, 'packingDescription', e.target.value)} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField required={is705} value={item.hsCode || ''} label={<>HS Code{' '}<Tooltip title='Enter the HS Code (minimum 6 digits) used to classify the goods. Additional rules on HS Code format (e.g., 8- or 10-digit codes) will eventually be introduced and communicated, as required by NAIC and possibly other UAE government agencies. If number of HS codes for the same goods item exceeded 1 code, enter the HS Code in comma separated format.'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' inputProps={{ maxLength: 94 }} error={!!goodsFieldErrors[idx]?.hsCode} helperText={goodsFieldErrors[idx]?.hsCode || 'Required when no FF consolidation (Type 705)'} onChange={(e) => { updateGoodsField(idx, 'hsCode', e.target.value); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'hsCode') }} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <SearchableCountryField value={item.goodsItemCountryOfOrigin || ''} label='Goods Item Country of Origin' disabled={disabled} onChange={(e) => updateGoodsField(idx, 'goodsItemCountryOfOrigin', e.target.value)} />
              </CngGridItem>
              <CngGridItem xs={12}>
                <CngTextField required={is705} value={item.goodsItemDescription || ''} label='Goods Item Description' disabled={disabled} size='small' inputProps={{ maxLength: 2560 }} multiline rows={2} error={!!goodsFieldErrors[idx]?.goodsItemDescription} helperText={goodsFieldErrors[idx]?.goodsItemDescription} onChange={(e) => { updateGoodsField(idx, 'goodsItemDescription', e.target.value.toUpperCase()); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'goodsItemDescription') }} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField required value={item.goodsItemGrossWeight ?? ''} label='Goods Item Gross Weight' disabled={disabled} size='small' inputProps={{ maxLength: 18 }} error={!!goodsFieldErrors[idx]?.goodsItemGrossWeight} helperText={goodsFieldErrors[idx]?.goodsItemGrossWeight} onChange={(e) => { updateGoodsField(idx, 'goodsItemGrossWeight', e.target.value.replace(/[^0-9]/g, '')); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'goodsItemGrossWeight') }} />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <ClearableCngSelectField required value={item.grossWeightUnitCode || ''} label='Gross Weight Unit Code' disabled={disabled} size='small' items={WEIGHT_UNIT} error={!!goodsFieldErrors[idx]?.grossWeightUnitCode} helperText={goodsFieldErrors[idx]?.grossWeightUnitCode} onChange={(e) => { updateGoodsField(idx, 'grossWeightUnitCode', e.target.value); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'grossWeightUnitCode') }} />
              </CngGridItem>
            </Grid>
            {/* Additional Goods Info collapsible */}
            <Box mt={2}>
              <Box onClick={() => setAdditionalOpen(p => ({ ...p, [idx]: !p[idx] }))} display='flex' alignItems='center'
                style={{ padding: '8px 12px', background: '#f5f4f0', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.09)', gap: 8 }}>
                <FontAwesomeIcon icon={['fal', 'circle-plus']} style={{ fontSize: 13, flexShrink: 0 }} />
                <Typography variant='subtitle2' style={{ flex: 1, fontSize: 13 }}>Additional Goods Info</Typography>
                <Chip label='Optional' size='small' style={{ fontSize: 11, height: 20 }} />
                <FontAwesomeIcon icon={['fal', additionalOpen[idx] ? 'chevron-up' : 'chevron-down']} style={{ fontSize: 12 }} />
              </Box>
              <Collapse in={!!additionalOpen[idx]}>
                <Box mt={1} p={2} border={1} borderColor='divider' borderRadius={8}>
                  <Grid container spacing={1}>
                    <CngGridItem xs={12}><CngTextField value={item.additionalInformationRelatedToGoodsItem || ''} label={<>Additional Goods Details{' '}<Tooltip title='Provide any additional information related to this goods item (e.g., special handling, descriptions, or regulatory remarks).'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' inputProps={{ maxLength: 512 }} multiline rows={2} onChange={(e) => updateGoodsField(idx, 'additionalInformationRelatedToGoodsItem', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={3}><CngTextField value={item.goodsItemNetWeight ?? ''} label='Goods Item Net Weight' disabled={disabled} size='small' inputProps={{ maxLength: 18 }} error={!!goodsFieldErrors[idx]?.goodsItemNetWeight} helperText={goodsFieldErrors[idx]?.goodsItemNetWeight} onChange={(e) => { updateGoodsField(idx, 'goodsItemNetWeight', e.target.value.replace(/[^0-9]/g, '')); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'goodsItemNetWeight') }} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={3}><ClearableCngSelectField required={!!item.goodsItemNetWeight} value={item.netWeightUnitCode || ''} label='Net Weight Unit Code' disabled={disabled} size='small' items={NET_WEIGHT_UNIT} onChange={(e) => updateGoodsField(idx, 'netWeightUnitCode', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={3}><CngTextField value={item.volume ?? ''} label='Volume' disabled={disabled} size='small' inputProps={{ maxLength: 18 }} error={!!goodsFieldErrors[idx]?.volume} helperText={goodsFieldErrors[idx]?.volume} onChange={(e) => { updateGoodsField(idx, 'volume', e.target.value.replace(/[^0-9]/g, '')); if (clearGoodsFieldError) clearGoodsFieldError(idx, 'volume') }} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={3}><ClearableCngSelectField required={!!item.volume} value={item.volumeUnitCode || ''} label='Volume Unit Code' disabled={disabled} size='small' items={VOLUME_UNIT} onChange={(e) => updateGoodsField(idx, 'volumeUnitCode', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}><ClearableCngSelectField value={item.packingInstructionsCode || ''} label='Packing Instructions Code' disabled={disabled} size='small' items={PACKING_INSTRUCTIONS_CODE} onChange={(e) => updateGoodsField(idx, 'packingInstructionsCode', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}><CngTextField value={item.marksAndLabels || ''} label='Marks and Labels' disabled={disabled} size='small' inputProps={{ maxLength: 35 }} onChange={(e) => updateGoodsField(idx, 'marksAndLabels', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}><CngTextField value={item.locationName || ''} label='Location Name' disabled={disabled} size='small' inputProps={{ maxLength: 256 }} onChange={(e) => updateGoodsField(idx, 'locationName', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}><ClearableCngSelectField value={item.handlingInstructionDescriptionCode || ''} label='Handling Instruction Description Code' disabled={disabled} size='small' items={HANDLING_INSTRUCTION_CODE} onChange={(e) => updateGoodsField(idx, 'handlingInstructionDescriptionCode', e.target.value)} /></CngGridItem>
                    <CngGridItem xs={12} sm={6} lg={4}><CngTextField value={item.handlingInstructionDescription || ''} label='Handling Instruction Description' disabled={disabled} size='small' inputProps={{ maxLength: 512 }} onChange={(e) => updateGoodsField(idx, 'handlingInstructionDescription', e.target.value)} /></CngGridItem>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
            {/* Dangerous Goods collapsible */}
            <Box mt={1}>
              <Box onClick={() => setDgOpen(p => ({ ...p, [idx]: !p[idx] }))} display='flex' alignItems='center'
                style={{ padding: '8px 12px', background: '#f5f4f0', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.09)', gap: 8 }}>
                <FontAwesomeIcon icon={['fal', 'radiation']} style={{ fontSize: 13, flexShrink: 0 }} />
                <Typography variant='subtitle2' style={{ flex: 1, fontSize: 13 }}>Dangerous Goods</Typography>
                <Chip label='Conditional' size='small' style={{ fontSize: 11, height: 20 }} />
                <Tooltip title='Allow to add up to 99 DG Info sets per good.'><span style={{ pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 13, opacity: 0.5 }} /></span></Tooltip>
                <FontAwesomeIcon icon={['fal', dgOpen[idx] ? 'chevron-up' : 'chevron-down']} style={{ fontSize: 12 }} />
              </Box>
              <Collapse in={!!dgOpen[idx]}>
                <Box mt={1} p={2} border={1} borderColor='divider' borderRadius={8}>
                  {(item.dangerousGoodsInfo || []).map((dg, dgIdx) => (
                    <Box key={dgIdx} mb={2} border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
                      <Box display='flex' alignItems='center' justifyContent='space-between'
                        style={{ padding: '8px 12px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                        <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                          <FontAwesomeIcon icon={['fal', 'biohazard']} style={{ fontSize: 13, opacity: 0.6 }} />
                          <Typography variant='subtitle2'>DG Entry #{dgIdx + 1}</Typography>
                        </Box>
                        {!disabled && <IconButton size='small' onClick={() => removeDgInfo(idx, dgIdx)}><FontAwesomeIcon icon={['fal', 'trash']} /></IconButton>}
                      </Box>
                      <Box p={2}>
                        <Grid container spacing={2}>
                          <CngGridItem xs={12} sm={6} md={4}><SearchableSelectField required value={dg.dangerousGoodsHazardClassCode || ''} label='Dangerous Goods Hazard Class Codes' disabled={disabled} items={DG_HAZARD_CLASS} error={!!goodsFieldErrors[idx]?.dangerousGoodsInfo?.[dgIdx]?.dangerousGoodsHazardClassCode} helperText={goodsFieldErrors[idx]?.dangerousGoodsInfo?.[dgIdx]?.dangerousGoodsHazardClassCode} onChange={(e) => { updateDgField(idx, dgIdx, 'dangerousGoodsHazardClassCode', e.target.value); if (e.target.value) clearDgFieldError(idx, dgIdx, 'dangerousGoodsHazardClassCode') }} /></CngGridItem>
                          <CngGridItem xs={12} sm={6} md={4}><CngTextField required value={dg.unitedNationsDangerousGoodsIdentifier || ''} label={<>United Nations Dangerous Goods Identifier{' '}<Tooltip title='Provide the value in UNDG code identifier format (e.g. 3090).'><span style={{ cursor: 'help', pointerEvents: 'auto' }}><FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} /></span></Tooltip></>} disabled={disabled} size='small' inputProps={{ maxLength: 4 }} error={!!goodsFieldErrors[idx]?.dangerousGoodsInfo?.[dgIdx]?.unitedNationsDangerousGoodsIdentifier} helperText={goodsFieldErrors[idx]?.dangerousGoodsInfo?.[dgIdx]?.unitedNationsDangerousGoodsIdentifier} onChange={(e) => { updateDgField(idx, dgIdx, 'unitedNationsDangerousGoodsIdentifier', e.target.value); if (e.target.value) clearDgFieldError(idx, dgIdx, 'unitedNationsDangerousGoodsIdentifier') }} /></CngGridItem>
                          <CngGridItem xs={12} sm={6} md={4}><CngTextField value={dg.dangerousGoodsFlashpointDescription || ''} label='Dangerous Goods Flashpoint Description' disabled={disabled} size='small' inputProps={{ maxLength: 8 }} onChange={(e) => updateDgField(idx, dgIdx, 'dangerousGoodsFlashpointDescription', e.target.value)} /></CngGridItem>
                          <CngGridItem xs={12} sm={6} md={4}><CngTextField value={dg.shipmentFlashpointDegree ?? ''} label='Shipment Flashpoint Degree' disabled={disabled} size='small' inputProps={{ maxLength: 3 }} onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault() }} onChange={(e) => updateDgField(idx, dgIdx, 'shipmentFlashpointDegree', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))} /></CngGridItem>
                          <CngGridItem xs={12} sm={6} md={4}><ClearableCngSelectField required={!!dg.shipmentFlashpointDegree} value={dg.flashpointUnitCode || ''} label='Flashpoint Unit code' disabled={disabled} size='small' items={TEMPERATURE_UNIT} error={!!goodsFieldErrors[idx]?.dangerousGoodsInfo?.[dgIdx]?.flashpointUnitCode} helperText={goodsFieldErrors[idx]?.dangerousGoodsInfo?.[dgIdx]?.flashpointUnitCode} onChange={(e) => { updateDgField(idx, dgIdx, 'flashpointUnitCode', e.target.value); if (e.target.value) clearDgFieldError(idx, dgIdx, 'flashpointUnitCode') }} /></CngGridItem>
                          <CngGridItem xs={12} sm={6} md={4}><ClearableCngSelectField value={dg.packagingDangerLevelCode || ''} label='Packaging Danger Level Code' disabled={disabled} size='small' items={DG_PACKAGING_DANGER_LEVEL} onChange={(e) => updateDgField(idx, dgIdx, 'packagingDangerLevelCode', e.target.value)} /></CngGridItem>
                        </Grid>
                      </Box>
                    </Box>
                  ))}
                  {!disabled && <CngButton color='default' size='small' onClick={() => addDgInfo(idx)} startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}>Add Dangerous Goods Info</CngButton>}
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>
        ))}
        {!disabled && goodsInfo.length < 9999 && (
          <CngButton color='primary' size='small' onClick={() => addListItem('goodsInfo', {
            containerNumber: '', goodsItemNumber: String(goodsInfo.length + 1).padStart(6, '0'),
            totalPackageQuantity: '', packageType: '', packageTypeDescription: '',
            packingDescription: '', packingInstructionsCode: '',
            hsCode: '', goodsItemCountryOfOrigin: '',
            goodsItemGrossWeight: '', grossWeightUnitCode: '',
            goodsItemNetWeight: '', netWeightUnitCode: '',
            volume: '', volumeUnitCode: '',
            handlingInstructionDescriptionCode: '', handlingInstructionDescription: '',
            locationName: '', marksAndLabels: '',
            goodsItemDescription: '', additionalInformationRelatedToGoodsItem: '',
                        dangerousGoodsInfo: []
          })} startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}>
            Add Goods Item
          </CngButton>
        )}
      </CardContent>
    </CngSection>
  )
}

function ReviewField({ label, value }) {
  return (
    <Box display='flex' style={{ gap: 6, marginBottom: 4 }}>
      <Typography variant='caption' color='textSecondary' style={{ minWidth: 160, flexShrink: 0 }}>{label}</Typography>
      <Typography variant='caption' style={{ fontWeight: 500 }}>{value || '—'}</Typography>
    </Box>
  )
}

function ReviewSectionCard({ icon, title, count, chipColor, onClick }) {
  return (
    <Box onClick={onClick} display='flex' alignItems='center'
      style={{ padding: '12px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid rgba(0,0,0,0.09)', cursor: 'pointer', gap: 12 }}>
      <FontAwesomeIcon icon={['fal', icon]} style={{ fontSize: 16, opacity: 0.6, flexShrink: 0 }} />
      <Box flex={1}>
        <Typography variant='subtitle2'>{title}</Typography>
        <Typography variant='caption' color='textSecondary'>{count} record{count !== 1 ? 's' : ''}</Typography>
      </Box>
      <Chip label={count} size='small' style={{ fontSize: 11, height: 20, background: chipColor || '#e0e0e0', color: count > 0 ? '#fff' : '#757575', fontWeight: 600 }} />
      <FontAwesomeIcon icon={['fal', 'arrow-up-right-from-square']} style={{ fontSize: 12, opacity: 0.4 }} />
    </Box>
  )
}

function Step7ReviewSubmit({ getValues }) {
  const { watch } = useFormContext()
  const values = watch()
  const [dialog, setDialog] = useState(null) // 'parties' | 'containers' | 'goods'

  const parties = values.partiesInfo || []
  const containers = values.containerDetails || []
  const goods = values.goodsInfo || []

  return (
    <CngSection title='Review & Submit'>
      <CardContent>
        <Box mb={3}>
          <Typography variant='body2' color='textSecondary'>Review all sections before submitting to XBSNG.</Typography>
        </Box>

        {/* Summary cards row 1 */}
        <Grid container spacing={2}>
          {/* Bill Header */}
          <CngGridItem xs={12} sm={6}>
            <Box style={{ padding: '12px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid rgba(0,0,0,0.09)' }}>
              <Box display='flex' alignItems='center' style={{ gap: 8, marginBottom: 10 }}>
                <FontAwesomeIcon icon={['fal', 'file-lines']} style={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant='subtitle2'>Bill Header</Typography>
              </Box>
              <ReviewField label='Master Bill No' value={values.billNo} />
              <ReviewField label='MPCI Document ID / Transaction ID' value={values.mpciDocId} />
              <ReviewField label='Filing Role' value={values.filingOperator} />
              <ReviewField label='Shipment Type' value={values.shipmentType} />
              <ReviewField label='Processing Indicator' value={values.processingIndicator} />
            </Box>
          </CngGridItem>

          {/* Vessel & Voyage */}
          <CngGridItem xs={12} sm={6}>
            <Box style={{ padding: '12px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid rgba(0,0,0,0.09)' }}>
              <Box display='flex' alignItems='center' style={{ gap: 8, marginBottom: 10 }}>
                <FontAwesomeIcon icon={['fal', 'ship']} style={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant='subtitle2'>Vessel & Voyage</Typography>
              </Box>
              <ReviewField label='Vessel Name' value={values.vesselName} />
              <ReviewField label='Voyage Number' value={values.voyageNumber} />
              <ReviewField label='IMO Number' value={values.imoNumber} />
              <ReviewField label='Carrier Identifier' value={values.carrierIdentifier} />
              <ReviewField label='ETD (Last Foreign Port)' value={values.etdAtTheLastForeignPort} />
              <ReviewField label='ETA (First UAE Seaport)' value={values.etaAtTheFirstUaeSeaport} />
            </Box>
          </CngGridItem>

          {/* Consignment */}
          <CngGridItem xs={12} sm={6}>
            <Box style={{ padding: '12px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid rgba(0,0,0,0.09)' }}>
              <Box display='flex' alignItems='center' style={{ gap: 8, marginBottom: 10 }}>
                <FontAwesomeIcon icon={['fal', 'anchor']} style={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant='subtitle2'>Consignment</Typography>
              </Box>
              <ReviewField label='Port of Loading' value={values.portOfLoading} />
              <ReviewField label='Port of Discharge' value={values.portOfDischarge} />
              <ReviewField label='Place of Receipt' value={values.placeOfReceiptCountry} />
              <ReviewField label='Place of Delivery' value={values.placeOfDeliveryCountry} />
              <ReviewField label='Emirate' value={values.emirate} />
              <ReviewField label='Transshipment Ports' value={(values.transshipmentPorts || []).length > 0 ? `${values.transshipmentPorts.length} port(s)` : null} />
            </Box>
          </CngGridItem>

          {/* Monetary */}
          <CngGridItem xs={12} sm={6}>
            <Box style={{ padding: '12px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid rgba(0,0,0,0.09)' }}>
              <Box display='flex' alignItems='center' style={{ gap: 8, marginBottom: 10 }}>
                <FontAwesomeIcon icon={['fal', 'dollar-sign']} style={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant='subtitle2'>Monetary</Typography>
              </Box>
              {(values.monetaryDetails || []).length === 0
                ? <Typography variant='caption' color='textSecondary'>No monetary entries</Typography>
                : (values.monetaryDetails || []).map((m, i) => (
                  <Box key={i} mb={0.5}>
                    <Typography variant='caption'>{i + 1}. {m.monetaryAmountType || '—'} — {m.monetaryAmount ?? '—'} {m.currencyCode || ''}</Typography>
                  </Box>
                ))
              }
            </Box>
          </CngGridItem>
        </Grid>

        {/* Clickable detail sections */}
        <Box mt={2}>
          <Typography variant='subtitle2' style={{ marginBottom: 8 }}>Detail Sections</Typography>
          <Grid container spacing={1}>
            <CngGridItem xs={12} sm={4}>
              <ReviewSectionCard
                icon='users'
                title='Parties'
                count={parties.length}
                chipColor={parties.length > 0 ? '#2e7d32' : '#bdbdbd'}
                onClick={() => setDialog('parties')}
              />
            </CngGridItem>
            <CngGridItem xs={12} sm={4}>
              <ReviewSectionCard
                icon='container-storage'
                title='Containers'
                count={containers.length}
                chipColor={containers.length > 0 ? '#1565c0' : '#bdbdbd'}
                onClick={() => setDialog('containers')}
              />
            </CngGridItem>
            <CngGridItem xs={12} sm={4}>
              <ReviewSectionCard
                icon='boxes-stacked'
                title='Goods'
                count={goods.length}
                chipColor={goods.length > 0 ? '#6a1b9a' : '#bdbdbd'}
                onClick={() => setDialog('goods')}
              />
            </CngGridItem>
          </Grid>
        </Box>
      </CardContent>

      {/* Parties Dialog */}
      <Dialog open={dialog === 'parties'} onClose={() => setDialog(null)} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box display='flex' alignItems='center' style={{ gap: 10 }}>
            <FontAwesomeIcon icon={['fal', 'users']} style={{ fontSize: 16 }} />
            <Typography variant='h6'>Parties Review</Typography>
            <Chip label={`${parties.length} record${parties.length !== 1 ? 's' : ''}`} size='small' style={{ marginLeft: 4 }} />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {parties.length === 0
            ? <Typography variant='body2' color='textSecondary'>No parties added.</Typography>
            : parties.map((p, i) => (
              <Box key={i} mb={2}>
                <Box display='flex' alignItems='center' style={{ gap: 8, marginBottom: 6 }}>
                  <Chip label={p.partyType || 'OTHER'} size='small' style={{ fontSize: 11, height: 20, background: '#1565c0', color: '#fff' }} />
                  <Typography variant='subtitle2'>{p.partyName || '—'}</Typography>
                </Box>
                <Grid container spacing={1}>
                  <CngGridItem xs={6} sm={4}><ReviewField label='Party ID' value={p.partyId} /></CngGridItem>
                  <CngGridItem xs={6} sm={4}><ReviewField label='ID Type' value={p.partyIdentificationType} /></CngGridItem>
                  <CngGridItem xs={6} sm={4}><ReviewField label='Country' value={p.country} /></CngGridItem>
                  <CngGridItem xs={6} sm={4}><ReviewField label='City' value={p.city} /></CngGridItem>
                  <CngGridItem xs={12} sm={8}><ReviewField label='Address' value={[p.address1, p.address2, p.address3].filter(Boolean).join(', ')} /></CngGridItem>
                </Grid>
                {i < parties.length - 1 && <Divider style={{ marginTop: 12 }} />}
              </Box>
            ))
          }
        </DialogContent>
        <DialogActions>
          <CngButton color='secondary' size='small' onClick={() => setDialog(null)}>Close</CngButton>
        </DialogActions>
      </Dialog>

      {/* Containers Dialog */}
      <Dialog open={dialog === 'containers'} onClose={() => setDialog(null)} maxWidth='lg' fullWidth>
        <DialogTitle>
          <Box display='flex' alignItems='center' style={{ gap: 10 }}>
            <FontAwesomeIcon icon={['fal', 'container-storage']} style={{ fontSize: 16 }} />
            <Typography variant='h6'>Containers Review</Typography>
            <Chip label={`${containers.length} record${containers.length !== 1 ? 's' : ''}`} size='small' style={{ marginLeft: 4 }} />
          </Box>
        </DialogTitle>
                <DialogContent dividers style={{ padding: 0 }}>
          {containers.length === 0
            ? <Box p={3}><Typography variant='body2' color='textSecondary'>No containers added.</Typography></Box>
            : <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>#</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Container No</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Size</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Load Status</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Seal ID</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {containers.map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell><Typography variant='caption'>{i + 1}</Typography></TableCell>
                      <TableCell><Typography variant='caption' style={{ fontFamily: 'monospace' }}>{c.containerNumber || '—'}</Typography></TableCell>
                      <TableCell><Typography variant='caption'>{c.containerSize || '—'}</Typography></TableCell>
                      <TableCell><Typography variant='caption'>{c.containerLoadStatus || '—'}</Typography></TableCell>
                      <TableCell><Typography variant='caption'>{c.sealIdentifier || '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          }
        </DialogContent>
        <DialogActions>
          <CngButton color='secondary' size='small' onClick={() => setDialog(null)}>Close</CngButton>
        </DialogActions>
      </Dialog>

      {/* Goods Dialog */}
      <Dialog open={dialog === 'goods'} onClose={() => setDialog(null)} maxWidth='lg' fullWidth>
        <DialogTitle>
          <Box display='flex' alignItems='center' style={{ gap: 10 }}>
            <FontAwesomeIcon icon={['fal', 'boxes-stacked']} style={{ fontSize: 16 }} />
            <Typography variant='h6'>Goods Review</Typography>
            <Chip label={`${goods.length} record${goods.length !== 1 ? 's' : ''}`} size='small' style={{ marginLeft: 4 }} />
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {goods.length === 0
            ? <Box p={3}><Typography variant='body2' color='textSecondary'>No goods added.</Typography></Box>
            : <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>#</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Container No</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>HS Code</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Packages</Typography></TableCell>
                    <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Description</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {goods.map((g, i) => (
                    <TableRow key={i} hover>
                      <TableCell><Typography variant='caption'>{i + 1}</Typography></TableCell>
                      <TableCell><Typography variant='caption' style={{ fontFamily: 'monospace' }}>{g.containerNumber || '—'}</Typography></TableCell>
                      <TableCell><Typography variant='caption'>{g.hsCode || '—'}</Typography></TableCell>
                      <TableCell><Typography variant='caption'>{g.totalPackageQuantity || '—'}</Typography></TableCell>
                      <TableCell><Typography variant='caption'>{g.goodsItemDescription || '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          }
        </DialogContent>
        <DialogActions>
          <CngButton color='secondary' size='small' onClick={() => setDialog(null)}>Close</CngButton>
        </DialogActions>
      </Dialog>
    </CngSection>
  )
}

// consignmentLocationIdentificationType code mapping
const CONSIGNMENT_TYPE = { PORT_OF_LOADING: '9', PORT_OF_DISCHARGE: '12', PLACE_OF_RECEIPT: '88', PLACE_OF_DELIVERY: '7', TRANSSHIPMENT: '13', EMIRATE: '96' }

function toClientDataFormat(serverData) {
  const v = serverData.vesselInfo || {}
  const consignmentDetails = serverData.consignmentDetails || []
  const findConsignment = (typeCode) => consignmentDetails.find(c => c.consignmentLocationIdentificationType === typeCode) || {}
  const polEntry         = findConsignment(CONSIGNMENT_TYPE.PORT_OF_LOADING)
  const podEntry         = findConsignment(CONSIGNMENT_TYPE.PORT_OF_DISCHARGE)
  const porEntry         = findConsignment(CONSIGNMENT_TYPE.PLACE_OF_RECEIPT)
  const podlEntry        = findConsignment(CONSIGNMENT_TYPE.PLACE_OF_DELIVERY)
  const transshipEntries = consignmentDetails.filter(c => c.consignmentLocationIdentificationType === CONSIGNMENT_TYPE.TRANSSHIPMENT)
  const emirateEntry     = findConsignment(CONSIGNMENT_TYPE.EMIRATE)

  const partiesInfo = (serverData.partiesInfo || [])
    .filter(p => p && p.partyType)
    .map(p => ({
      ...p,
      address1: p.street || '',
      address2: p.streetNumber || '',
      address3: p.postalCode || '',
      partyIdentificationType: p.partyIdentificationType || (p.partyType === 'CZ' ? 'SP' : '')
    }))

  return {
    ...serverData,
    onBehalfOfOrgId: serverData.orgId ?? null,
    billType: serverData.billType || 'M',
    filingOperator: serverData.filingOperator || '',
    documentType: serverData.documentType || '',
    processingIndicator: serverData.processingIndicator || '',
    shipmentType: serverData.shipmentType || '',
    billNo: serverData.masterBillNo || serverData.billNo || '',
    mpciDocId: serverData.mpciDocId || '',
    shippingLineMpciPartyId: serverData.shippingLineMpciPartyId || '',
    uaeFFSubColoaderMpciPartyId: serverData.uaeFfSubCoLoaderMpciPartyId || serverData.uaeFFSubColoaderMpciPartyId || '',
    placeOfBillIssueCountry: serverData.placeOfBillIssueCountry || '',
    billIssueLocationName: serverData.billIssueLocationName || '',
    bolIssueDate: serverData.bolIssueDate ? moment(serverData.bolIssueDate).format('YYYY-MM-DD HH:mm') : null,
    freightPayableCountry: serverData.freightPayableCountry || '',
    freightLocationName: serverData.freightLocationName || '',
    sobDate: serverData.sobDate ? moment(serverData.sobDate).format('YYYY-MM-DD HH:mm') : null,
    shipmentDescription: serverData.shipmentDescription || '',
    forwardingNotes: serverData.forwardingNotes || '',
    functionType: serverData.functionType || 'NEW',
    reasonForUpdatingFiling: serverData.reasonForUpdatingFiling || '',
    voyageNumber: v.voyageNumber || '',
    carrierIdentifier: v.carrierIdentifier || '',
    imoNumber: v.imoNumber || '',
    vesselName: v.vesselName || '',
    vesselFlagCountry: v.vesselFlagCountry || '',
    vesselOwnershipIndicatorCode: v.vesselOwnershipIndicatorCode || '',
    vesselPowerTypeCode: v.vesselPowerTypeCode || '',
    vesselPowerTypeDescription: v.vesselPowerTypeDescription || '',
    vesselLastForeignPortOfDeparture: v.vesselLastForeignPortOfDeparture || '',
    vesselFirstUaePortOfArrival: v.vesselFirstPortOfArrival || '',
    etdAtTheLastForeignPort: v.etdAtTheLastForeignPort ? moment(v.etdAtTheLastForeignPort).format('YYYY-MM-DD HH:mm') : null,
    atdAtTheLastForeignPort: v.atdAtTheLastForeignPort ? moment(v.atdAtTheLastForeignPort).format('YYYY-MM-DD HH:mm') : null,
    etaAtTheFirstUaeSeaport: v.etaAtTheFirstUaeSeaport ? moment(v.etaAtTheFirstUaeSeaport).format('YYYY-MM-DD HH:mm') : null,
    saveVesselMaster: v.saveVesselMaster || false,
    portOfLoading: polEntry.consignmentLocation || '',
    portOfDischarge: podEntry.consignmentLocation || '',
    placeOfReceiptCountry: porEntry.consignmentLocation || '',
    placeOfReceiptName: porEntry.consignmentLocationName || '',
    placeOfDeliveryCountry: podlEntry.consignmentLocation || '',
    placeOfDeliveryName: podlEntry.consignmentLocationName || '',
    transshipmentPorts: transshipEntries.map(t => ({ unlocCode: t.consignmentLocation || '', locationName: t.consignmentLocationName || '' })),
    emirate: emirateEntry.consignmentLocation || serverData.emirate || '',
    monetaryDetails: serverData.monetaryDetails || [],
    partiesInfo,
    containerDetails: (serverData.containerDetails || []).map(c => ({
      ...c,
      reeferOn: !!(c.temperatureType || c.containerTemperatureDegree != null || c.temperatureMeasurementCode)
    })),
    goodsInfo: serverData.goodsInfo || [],
    supportingDocuments: serverData.supportingDocuments || [],
    status: serverData.status || ''
  }
}

function toLocalDateTime(val) {
  if (!val) return null
  // Moment.js object (from CngDateField via cng-web-lib MUI date picker)
  if (typeof val === 'object' && typeof val.format === 'function') {
    const formatted = val.format('YYYY-MM-DDTHH:mm:ss')
    return formatted === 'Invalid date' ? null : formatted
  }
  // Native JS Date object
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null
    const pad = (n) => String(n).padStart(2, '0')
    return `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(val.getDate())}T${pad(val.getHours())}:${pad(val.getMinutes())}:${pad(val.getSeconds())}`
  }
  if (typeof val !== 'string') return null
  const dateOnly = val.substring(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null
  if (val.length >= 19) return dateOnly + 'T' + val.substring(11, 19)
  if (val.length >= 16) return dateOnly + 'T' + val.substring(11, 16) + ':00'
  return dateOnly + 'T00:00:00'
}

function toInt(val) {
  if (val === null || val === undefined || val === '') return null
  const n = parseInt(val, 10)
  return isNaN(n) ? null : n
}

function toFloatOrNull(val) {
  if (val === null || val === undefined || val === '') return null
  const n = parseFloat(val)
  return isNaN(n) ? null : n
}

function toServerDataFormat(localData) {
  const {
    billNo,
    bolIssueDate,
    sobDate,
    voyageNumber, carrierIdentifier, imoNumber, vesselName,
    vesselFlagCountry, vesselOwnershipIndicatorCode, vesselPowerTypeCode,
    vesselPowerTypeDescription, vesselLastForeignPortOfDeparture,
    vesselFirstUaePortOfArrival, etdAtTheLastForeignPort,
    atdAtTheLastForeignPort, etaAtTheFirstUaeSeaport,
    portOfLoading, portOfDischarge,
    placeOfReceiptCountry, placeOfReceiptName,
    placeOfDeliveryCountry, placeOfDeliveryName,
    transshipmentPorts,
    uaeFFSubColoaderMpciPartyId,
    emirate: _emirate,
    consignmentDetails: _consignmentDetails,
    mpciDocId: _mpciDocId,
    supportingDocuments: _supportingDocuments,
    attachmentInfo: _attachmentInfo,
    status: _status,
    version: _version,
        // Explicitly pull out all array/nested fields + HBL-only fields so they don't leak into ...rest
    partiesInfo: _rawParties,
    containerDetails: _rawContainers,
    goodsInfo: _rawGoods,
    monetaryDetails: _rawMonetary,
    vesselInfo: _vesselInfo,
    // HBL-only fields — must NOT be sent in MBL payload
    uaeFreightForwarderMpciPartyId: _uaeFreightForwarderMpciPartyId,
    shiperMpciPartyId: _shiperMpciPartyId,
    submittedDate: _submittedDate,
    ...rest
  } = localData

  // Strip any keys that are undefined (e.g. stale RHF setValue calls with undefined key)
  const cleanRest = Object.fromEntries(
    Object.entries(rest).filter(([k]) => k !== 'undefined' && k !== undefined)
  )

  const consignmentDetails = []
  if (portOfLoading) consignmentDetails.push({ consignmentLocation: portOfLoading, consignmentLocationName: '', consignmentLocationIdentificationType: CONSIGNMENT_TYPE.PORT_OF_LOADING })
  if (portOfDischarge) consignmentDetails.push({ consignmentLocation: portOfDischarge, consignmentLocationName: '', consignmentLocationIdentificationType: CONSIGNMENT_TYPE.PORT_OF_DISCHARGE })
  if (placeOfReceiptCountry) consignmentDetails.push({ consignmentLocation: placeOfReceiptCountry, consignmentLocationName: placeOfReceiptName || '', consignmentLocationIdentificationType: CONSIGNMENT_TYPE.PLACE_OF_RECEIPT })
  if (placeOfDeliveryCountry) consignmentDetails.push({ consignmentLocation: placeOfDeliveryCountry, consignmentLocationName: placeOfDeliveryName || '', consignmentLocationIdentificationType: CONSIGNMENT_TYPE.PLACE_OF_DELIVERY })
  ;(transshipmentPorts || []).forEach(tp => {
    if (tp.unlocCode) consignmentDetails.push({ consignmentLocation: tp.unlocCode, consignmentLocationName: tp.locationName || '', consignmentLocationIdentificationType: CONSIGNMENT_TYPE.TRANSSHIPMENT })
  })
  const emirate = localData.emirate || rest.emirate
  if (emirate) consignmentDetails.push({ consignmentLocation: emirate, consignmentLocationName: '', consignmentLocationIdentificationType: CONSIGNMENT_TYPE.EMIRATE })

  const partiesInfo = (_rawParties || []).map(p => {
    const { _id, _isNewParty, address1, address2, address3, ...partyRest } = p
    const isCzCn = ['CZ', 'CN'].includes(partyRest.partyType)
    return { ...partyRest, street: address1 || null, streetNumber: (isCzCn && !address2) ? '-' : (address2 || null), postalCode: (isCzCn && !address3) ? '-' : (address3 || null), partyIdentificationType: partyRest.partyIdentificationType || (partyRest.partyType === 'CZ' ? 'SP' : null) }
  })

  const monetaryDetails = (_rawMonetary || []).map(m => {
    const { _id, ...mRest } = m
    return { ...mRest, monetaryAmount: toFloatOrNull(m.monetaryAmount) }
  })

  const containerDetails = (_rawContainers || []).map(c => {
    const { _id, reeferOn, ...cRest } = c
    return {
      ...cRest,
      containerVerifiedGrossMass: toFloatOrNull(c.containerVerifiedGrossMass),
      containerTemperatureDegree: reeferOn ? toFloatOrNull(c.containerTemperatureDegree) : null,
      temperatureType:            reeferOn ? (c.temperatureType || null) : null,
      temperatureMeasurementCode: reeferOn ? (c.temperatureMeasurementCode || null) : null,
      containerStatusDescription: c.containerStatusDescription || null,
      serviceRequirementCode:     c.serviceRequirementCode || null,
      cargoType:                  c.cargoType || null,
      sealingPartyCode:           c.sealingPartyCode || null,
      sealingPartyName:           c.sealingPartyName || null,
      sealType:                   c.sealType || null
    }
  })

  const goodsInfo = (_rawGoods || []).map((g, idx) => {
    const { _id, ...gRest } = g
    return {
      ...gRest,
      goodsItemNumber:      idx + 1,
      totalPackageQuantity: toInt(g.totalPackageQuantity),
      packageType:                              g.packageType || null,
      packageTypeDescription:                  g.packageTypeDescription || null,
      packingDescription:                      g.packingDescription || null,
      additionalInformationRelatedToGoodsItem: g.additionalInformationRelatedToGoodsItem || null,
      handlingInstructionDescriptionCode:      g.handlingInstructionDescriptionCode || null,
      handlingInstructionDescription:          g.handlingInstructionDescription || null,
      doesPackageToBeSplit:                    g.doesPackageToBeSplit || null,
      locationName:                            g.locationName || null,
      marksAndLabels:                          g.marksAndLabels || null,
      goodsItemGrossWeight:                    g.goodsItemGrossWeight || null,
      grossWeightUnitCode:                     g.grossWeightUnitCode || null,
      goodsItemNetWeight:                      g.goodsItemNetWeight || null,
      netWeightUnitCode:                       g.netWeightUnitCode || null,
      volume:                                  g.volume || null,
      volumeUnitCode:                          g.volumeUnitCode || null,
      packingInstructionsCode:                 g.packingInstructionsCode || null,
      dangerousGoodsInfo: (g.dangerousGoodsInfo || []).map(dg => ({
        ...dg,
        unitedNationsDangerousGoodsIdentifier: toInt(dg.unitedNationsDangerousGoodsIdentifier),
        shipmentFlashpointDegree:              toFloatOrNull(dg.shipmentFlashpointDegree)
      }))
    }
  })

  const payload = {
    ...cleanRest,
    billType: 'M',
    masterBillNo: billNo,
    bolIssueDate: toLocalDateTime(bolIssueDate),
    sobDate: toLocalDateTime(sobDate),
    vesselInfo: {
      voyageNumber:                     voyageNumber || null,
      carrierIdentifier:                carrierIdentifier || null,
      imoNumber:                        imoNumber || null,
      vesselName:                       vesselName || null,
      vesselFlagCountry:                vesselFlagCountry || null,
      vesselOwnershipIndicatorCode:     vesselOwnershipIndicatorCode || null,
      vesselPowerTypeCode:              vesselPowerTypeCode || null,
      vesselPowerTypeDescription:       vesselPowerTypeDescription || null,
      vesselLastForeignPortOfDeparture: vesselLastForeignPortOfDeparture || null,
      vesselFirstPortOfArrival:         vesselFirstUaePortOfArrival || null,
      etdAtTheLastForeignPort:          toLocalDateTime(etdAtTheLastForeignPort),
      atdAtTheLastForeignPort:          toLocalDateTime(atdAtTheLastForeignPort),
      etaAtTheFirstUaeSeaport:          toLocalDateTime(etaAtTheFirstUaeSeaport),
      saveVesselMaster:                 localData.saveVesselMaster || false
    },
    uaeFfSubCoLoaderMpciPartyId: uaeFFSubColoaderMpciPartyId || null,
    forwardingNotes: cleanRest.forwardingNotes || null,
    consignmentDetails,
    partiesInfo,
    monetaryDetails,
    containerDetails,
    goodsInfo
  }
  console.log('[MBL] toServerDataFormat payload:', payload)
  return payload
}

const FormProperties = {
  formikProps: FORMIK_PROPS,
  Fields,
  toClientDataFormat,
  toServerDataFormat,
  validateForSubmit
}

export default FormProperties